import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { emailExpress2ErrorMessage } from "@/lib/email-express-2-errors";
import { shortNameOf } from "@/lib/static-ad-buckets";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import type { User, BrandUser, Group } from "@shared/models/auth";
import type { Brand, AdUnitType, BrandAdUnitAccess, BrandAdUnitApproval, LandingPageTemplateType } from "@shared/schema";
import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Shield, Users, Building2, Plus, X, Check, ChevronLeft, LayoutGrid, LayoutTemplate, Globe, Lock, KeyRound, Trash2, UserPlus, Eye, EyeOff, Clock, CheckCircle2, XCircle, Pencil, Coins, Gauge, TrendingUp, Loader2, RefreshCw, ExternalLink, Link2, ImageIcon, RotateCcw, Settings, Wifi, Monitor, ChevronDown, ChevronRight, HardDrive, CloudUpload, DollarSign, FileUp, FileArchive, FileJson, FileText, Images, Laugh, FlaskConical, Video, BookText, Clapperboard, Compass } from "lucide-react";
import { BetaFeaturesManager } from "@/pages/admin-beta-features";
import CreativeStrategistAdminPane from "@/pages/admin-creative-strategist";
import { NarrativeV2Rollout } from "@/components/admin/narrative-v2-rollout";
import EmailSectionDevicesTab from "@/components/admin/email-section-devices-tab";
import VideoReferenceAssetsTab from "@/components/admin/video-reference-assets-tab";
import { AdsExpressConfigTab } from "@/components/ads-express-config-tab";
import { PersonaWorkflowsConfigTab } from "@/components/persona-workflows-config-tab";
import { CreativePerformanceConfigTab } from "@/components/admin/creative-performance-config-tab";
import { resolveInitialAdminTab } from "@/pages/admin-navigation";
import { Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import MemeSettingsSection from "@/components/meme-settings";
import { BrollExpressSettingsTab } from "@/components/broll-express-settings";
import StoryCategoriesManager from "@/components/story-categories-manager";
import VideoExpressStylesManager from "@/components/video-express-styles-manager";
import SlideshowStyleManager from "@/components/slideshow-express-style-manager";
import UgcExpressStyleManager from "@/components/ugc-express-style-manager";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AdsManagerDuplicationToggle } from "@/components/admin/ads-manager-duplication-toggle";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type AdminUser = User & { hasPassword?: boolean; groupName?: string | null };

type DomainStats = {
  totalRefs?: number;
  migrated?: number;
  missing?: number;
  failed?: number;
  details?: Array<{ table?: string; rowId?: string; field?: string; status?: string; message?: string }>;
};

type UploadsMigrateResult = {
  personas?: DomainStats;
  adUnits?: DomainStats;
  brandLogos?: DomainStats;
  products?: DomainStats;
  spyAds?: DomainStats;
  assets?: DomainStats;
};

type CheatsheetRow = {
  key: string;
  name: string;
  category: "asset" | "strategic";
  workflows: string[];
  models: string[];
  note: string;
  rows: number;
  source: "usage" | "pricing-config" | "none";
  configuredCostUsd: number | null;
  configuredCredits: number | null;
  configuredSource: "unit" | "model" | null;
  configuredModel: string | null;
  avgCostUsd: number;
  lowCostUsd: number;
  highCostUsd: number;
  avgCredits: number;
  lowCredits: number;
  highCredits: number;
};
type CheatsheetResp = { windowDays: number; rows: CheatsheetRow[] };

function fmtCredits(n: number): string {
  if (!isFinite(n) || n === 0) return "0";
  if (n < 1) return n.toFixed(2);
  if (n < 10) return n.toFixed(1);
  return Math.round(n).toLocaleString();
}

function CostCheatsheetTab() {
  const { data, isLoading, error, refetch, isFetching } = useQuery<CheatsheetResp>({
    // queryKey suffix bumped after removing the fake category markup so
    // any client still holding the old (×2 / ×10) response refetches
    // instead of showing stale credits.
    queryKey: ["/api/admin/cost-cheatsheet", "v2-no-markup"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/cost-cheatsheet");
      return res.json();
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
        <div className="font-medium text-foreground">How these numbers are calculated</div>
        <div>
          <span className="font-medium text-foreground">Base costs only.</span> Avg / range columns are pulled from the last 30 days of usage logs using the raw provider cost (<span className="font-mono">api_cost_usd</span> for token-based workflows + <span className="font-mono">raw_cost_usd</span> for unit-priced asset workflows). Credits shown = <span className="font-mono">baseCostUsd × 10</span> (1 credit = $0.10). Each user's own credit multiplier is applied on top at billing time and is <span className="font-medium">not</span> reflected here. Edit prices in <span className="font-medium">Model Settings</span>.
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2">
          <div className="font-medium text-destructive">Failed to load cheatsheet</div>
          <div className="text-xs font-mono text-muted-foreground break-all">{(error as Error).message}</div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="button-retry-cheatsheet">
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="rounded-md border p-6 text-sm text-muted-foreground text-center">
          No cheatsheet data returned.
        </div>
      ) : (
        <div className="rounded-md border" data-testid="table-cost-cheatsheet">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Media type</TableHead>
                <TableHead>Workflow / model</TableHead>
                <TableHead className="text-right">Avg credits</TableHead>
                <TableHead className="text-right">Range (credits)</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map(row => (
                <TableRow key={row.key} data-testid={`row-cheatsheet-${row.key}`}>
                  <TableCell className="align-top">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">
                      {row.category === "strategic" ? "Strategic Sim" : "Asset"}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap gap-1">
                      {row.workflows.map(w => (
                        <Badge key={w} variant="outline" className="font-mono text-[10px]">{w}</Badge>
                      ))}
                    </div>
                    {row.models.length > 0 && (
                      <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                        {row.models.slice(0, 2).join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right">
                    {row.source === "none" ? (
                      <span className="text-xs text-muted-foreground">no pricing config</span>
                    ) : (
                      <>
                        <div className="font-mono font-semibold" data-testid={`text-avg-credits-${row.key}`}>{fmtCredits(row.avgCredits)}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">${row.avgCostUsd.toFixed(4)}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {row.source === "usage"
                            ? "from recent usage"
                            : `from ${row.configuredSource === "unit" ? "unit" : "model"} pricing`}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right">
                    {row.source === "usage" && row.rows > 0 ? (
                      <>
                        <div className="font-mono text-sm">{fmtCredits(row.lowCredits)} – {fmtCredits(row.highCredits)}</div>
                        <div className="text-[10px] text-muted-foreground">{row.rows.toLocaleString()} log row{row.rows === 1 ? "" : "s"}</div>
                        {row.configuredCredits != null && (
                          <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                            config: {fmtCredits(row.configuredCredits)}
                          </div>
                        )}
                      </>
                    ) : row.source === "pricing-config" && row.configuredCredits != null ? (
                      <>
                        <div className="font-mono text-sm">{fmtCredits(row.configuredCredits)}</div>
                        <div className="text-[10px] text-muted-foreground">no recent usage</div>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-xs text-muted-foreground max-w-xs">{row.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

type EmailExpress2PromptSetting = {
  text: string;
  shippedDefault: string;
  isDefault: boolean;
  version: number;
  placeholders: string[];
};

export function EmailExpress2PromptTab() {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const promptQuery = useQuery<EmailExpress2PromptSetting>({
    queryKey: ["/api/admin/email-express-2-prompt"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/email-express-2-prompt");
      return res.json();
    },
  });

  useEffect(() => {
    if (promptQuery.data) setText(promptQuery.data.text);
  }, [promptQuery.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/admin/email-express-2-prompt", { text });
      return res.json() as Promise<EmailExpress2PromptSetting>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/admin/email-express-2-prompt"], data);
      setText(data.text);
      toast({ title: "Email Express 2.0 prompt saved" });
    },
    onError: (error: unknown) => {
      toast({ title: "Failed to save prompt", description: emailExpress2ErrorMessage(error), variant: "destructive" });
    },
  });

  const restore = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-express-2-prompt/restore-default", {});
      return res.json() as Promise<EmailExpress2PromptSetting>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/admin/email-express-2-prompt"], data);
      setText(data.text);
      toast({ title: "Shipped default restored" });
    },
    onError: (error: unknown) => {
      toast({ title: "Failed to restore default", description: emailExpress2ErrorMessage(error), variant: "destructive" });
    },
  });

  const clean = text.trim();
  const dirty = Boolean(promptQuery.data && text !== promptQuery.data.text);
  const busy = save.isPending || restore.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Express 2.0 Full Prompt Template</CardTitle>
        <CardDescription>
          Edit the complete brand-generic Email Express 2.0 prompt for initial designs and chat revisions.
          The shipped default follows the proven complete-block, 4–6 section, mobile-first campaign structure.
          Placeholders are replaced once with the current EE2 context; missing optional values become “Not provided.”
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {promptQuery.isLoading ? (
          <Skeleton className="h-[28rem] w-full" />
        ) : promptQuery.error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <p className="font-medium text-destructive">Failed to load the prompt</p>
            <p className="mt-1 text-muted-foreground">{emailExpress2ErrorMessage(promptQuery.error)}</p>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => promptQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="email-express-2-prompt">Complete Email Express 2.0 prompt template</Label>
              <Badge variant={promptQuery.data?.isDefault ? "secondary" : "outline"}>
                {promptQuery.data?.isDefault ? "Shipped default" : "Customized"}
              </Badge>
            </div>
            <Textarea
              id="email-express-2-prompt"
              value={text}
              onChange={(event) => setText(event.target.value)}
              className="min-h-[28rem] resize-y font-mono text-sm"
              disabled={busy}
              data-testid="textarea-email-express-2-prompt"
            />
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="text-xs font-medium text-foreground">Supported placeholders</p>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="list-email-express-2-placeholders">
                {(promptQuery.data?.placeholders || []).map((placeholder) => (
                  <code key={placeholder} className="rounded bg-background px-1.5 py-0.5 text-[11px]">
                    {`{{${placeholder}}}`}
                  </code>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Unknown placeholders are rejected. User-entered brief text is inserted literally, even if it contains braces.
              </p>
            </div>
            {!clean && <p className="text-sm text-destructive">Prompt text cannot be blank.</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => save.mutate()}
                disabled={!dirty || !clean || busy}
                data-testid="button-save-email-express-2-prompt"
              >
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save prompt
              </Button>
              <Button
                variant="outline"
                onClick={() => restore.mutate()}
                disabled={busy || promptQuery.data?.isDefault}
                data-testid="button-restore-email-express-2-prompt"
              >
                {restore.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Restore shipped default
              </Button>
              {dirty && <span className="text-sm text-muted-foreground">Unsaved changes</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

type TemplateStudioRulesSetting = {
  text: string;
  shippedDefault: string;
  isDefault: boolean;
  version: number;
};

export function TemplateStudioRulesTab() {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const queryKey = ["/api/admin/template-studio-rules"];
  const rulesQuery = useQuery<TemplateStudioRulesSetting>({
    queryKey,
    queryFn: async () => (await apiRequest("GET", queryKey[0])).json(),
  });

  useEffect(() => {
    if (rulesQuery.data) setText(rulesQuery.data.text);
  }, [rulesQuery.data]);

  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", queryKey[0], { text })).json() as Promise<TemplateStudioRulesSetting>,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      setText(data.text);
      toast({ title: "Template Studio rules saved" });
    },
    onError: (error: unknown) => {
      toast({ title: "Failed to save rules", description: emailExpress2ErrorMessage(error), variant: "destructive" });
    },
  });

  const restore = useMutation({
    mutationFn: async () => (await apiRequest("POST", `${queryKey[0]}/restore-default`, {})).json() as Promise<TemplateStudioRulesSetting>,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      setText(data.text);
      toast({ title: "Shipped Template Studio rules restored" });
    },
    onError: (error: unknown) => {
      toast({ title: "Failed to restore rules", description: emailExpress2ErrorMessage(error), variant: "destructive" });
    },
  });

  const clean = text.trim();
  const dirty = Boolean(rulesQuery.data && text !== rulesQuery.data.text);
  const busy = save.isPending || restore.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Template Studio Generation Rules</CardTitle>
        <CardDescription>
          These master rules are added before every new design and chat revision. Use them for requirements that
          should apply to every template, including text tokens, image placeholders, truthfulness, accessibility,
          structure, and hand-off conventions. Authors still provide the page-specific brief in Template Studio.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rulesQuery.isLoading ? (
          <Skeleton className="h-[28rem] w-full" />
        ) : rulesQuery.error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <p className="font-medium text-destructive">Failed to load the rules</p>
            <p className="mt-1 text-muted-foreground">{emailExpress2ErrorMessage(rulesQuery.error)}</p>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => rulesQuery.refetch()}>Retry</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="template-studio-rules">Rules applied to every Studio prompt</Label>
              <Badge variant={rulesQuery.data?.isDefault ? "secondary" : "outline"}>
                {rulesQuery.data?.isDefault ? "Shipped default" : "Customized"}
              </Badge>
            </div>
            <Textarea
              id="template-studio-rules"
              value={text}
              onChange={(event) => setText(event.target.value)}
              className="min-h-[28rem] resize-y font-mono text-sm"
              disabled={busy}
              data-testid="textarea-template-studio-rules"
            />
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Rules are stored separately from prompt starters. They apply globally to initial generations and
              revisions; changing them does not rewrite existing Studio versions.
            </div>
            {!clean && <p className="text-sm text-destructive">Rules cannot be blank.</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => save.mutate()} disabled={!dirty || !clean || busy} data-testid="button-save-template-studio-rules">
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save rules
              </Button>
              <Button variant="outline" onClick={() => restore.mutate()} disabled={busy || rulesQuery.data?.isDefault} data-testid="button-restore-template-studio-rules">
                {restore.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Restore shipped default
              </Button>
              {dirty && <span className="text-sm text-muted-foreground">Unsaved changes</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

import { GptImageVersionSetting } from "@/components/admin/gpt-image-version-setting";

export function ConceptBoardModelTab() {
  const { toast } = useToast();
  const key = ["/api/admin/concept-boards/model"];
  const reviewerKey = ["/api/admin/concept-boards/reviewer"];
  const { data, isLoading, error, refetch } = useQuery<{
    model: string; options: { id: string; label: string }[];
  }>({ queryKey: key, queryFn: async () => (await apiRequest("GET", key[0])).json() });
  const reviewer = useQuery<{ enabled: boolean }>({
    queryKey: reviewerKey, queryFn: async () => (await apiRequest("GET", reviewerKey[0])).json(),
  });
  const [choice, setChoice] = useState("");
  const [reviewerChoice, setReviewerChoice] = useState(false);
  useEffect(() => { if (data) setChoice(data.model); }, [data]);
  useEffect(() => { if (reviewer.data) setReviewerChoice(reviewer.data.enabled); }, [reviewer.data]);
  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", key[0], { model: choice })).json(),
    onSuccess: (updated) => {
      queryClient.setQueryData(key, updated);
      toast({ title: "Concept Board render model saved" });
    },
    onError: (e: Error) => toast({ title: "Could not save model", description: e.message, variant: "destructive" }),
  });
  const saveReviewer = useMutation({
    mutationFn: async () => (await apiRequest("PUT", reviewerKey[0], { enabled: reviewerChoice })).json() as Promise<{ enabled: boolean }>,
    onSuccess: (updated) => {
      queryClient.setQueryData(reviewerKey, updated);
      toast({ title: "Concept Board reviewer setting saved" });
    },
    onError: (e: Error) => toast({ title: "Could not save reviewer setting", description: e.message, variant: "destructive" }),
  });
  return <Card><CardHeader><CardTitle>Concept Boards</CardTitle>
    <CardDescription>Configure newly created boards. Existing boards keep their saved model and reviewer choice.</CardDescription>
  </CardHeader><CardContent className="space-y-4">
    {isLoading ? <Skeleton className="h-10 w-full" /> : error ?
      <div role="alert">Could not load the model setting. <Button variant="outline" onClick={() => refetch()}>Retry</Button></div> :
      <><Label htmlFor="concept-board-model">Board render model</Label>
        <Select value={choice} onValueChange={setChoice} disabled={save.isPending}>
          <SelectTrigger id="concept-board-model" data-testid="select-concept-board-model"><SelectValue placeholder="Select model" /></SelectTrigger>
          <SelectContent>{data?.options.map((option) => <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={() => save.mutate()} disabled={!choice || choice === data?.model || save.isPending} data-testid="button-save-concept-board-model">Save model</Button>
      </>}
    <div className="space-y-3 border-t pt-4">
      {reviewer.isLoading ? <Skeleton className="h-10 w-full" /> : reviewer.error ?
        <div role="alert">Could not load the reviewer setting. <Button variant="outline" onClick={() => reviewer.refetch()}>Retry</Button></div> :
        <>
          <div className="flex items-center gap-2">
            <Checkbox id="concept-board-reviewer" checked={reviewerChoice} disabled={saveReviewer.isPending}
              onCheckedChange={(checked) => setReviewerChoice(checked === true)} data-testid="checkbox-concept-board-reviewer" />
            <Label htmlFor="concept-board-reviewer">Enable board reviewer</Label>
          </div>
          <p className="text-sm text-muted-foreground">When enabled, an automated review runs after rendering and may suggest one correction. When disabled, boards are ready after rendering. Operators can still inspect and promote panels manually either way.</p>
          <Button onClick={() => saveReviewer.mutate()} disabled={reviewerChoice === reviewer.data?.enabled || saveReviewer.isPending}
            data-testid="button-save-concept-board-reviewer">Save reviewer setting</Button>
          {reviewerChoice !== reviewer.data?.enabled && <span className="ml-3 text-sm text-muted-foreground">Unsaved changes</span>}
        </>}
    </div>
  </CardContent></Card>;
}

function CreativeIterationsVideoPromptTab() {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const queryKey = ["/api/admin/creative-iterations-video-prompt"];
  const promptQuery = useQuery<Pick<EmailExpress2PromptSetting, "text" | "shippedDefault" | "isDefault"> & {
    videoModel: "seedance-2.5" | "minimax-h3";
  }>({
    queryKey,
    queryFn: async () => (await apiRequest("GET", queryKey[0])).json(),
  });

  useEffect(() => {
    if (promptQuery.data) {
      setText(promptQuery.data.text);
    }
  }, [promptQuery.data]);

  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", queryKey[0], {
      text,
      videoModel: promptQuery.data?.videoModel || "seedance-2.5",
    })).json(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      setText(data.text);
      toast({ title: "Creative Iterations video prompt saved" });
    },
    onError: (error: Error) => toast({ title: "Failed to save prompt", description: error.message, variant: "destructive" }),
  });
  const restore = useMutation({
    mutationFn: async () => (await apiRequest("POST", `${queryKey[0]}/restore-default`, {})).json(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      setText(data.text);
      toast({ title: "Shipped default restored" });
    },
    onError: (error: Error) => toast({ title: "Failed to restore default", description: error.message, variant: "destructive" }),
  });
  const clean = text.trim();
  const dirty = Boolean(promptQuery.data && text !== promptQuery.data.text);
  const busy = save.isPending || restore.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Creative Iterations Video Prompt</CardTitle>
        <CardDescription>
          Edit the shared prompt used to create four video variations. Any source-specific creative guidance is appended after this text.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {promptQuery.isLoading ? <Skeleton className="h-64 w-full" /> : promptQuery.error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <p className="font-medium text-destructive">Failed to load the prompt</p>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => promptQuery.refetch()}>Retry</Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              The generation model is selected automatically from each source length: MiniMax H3 Max through 15 seconds, then Seedance 2.5 through 30 seconds.
            </p>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="creative-iterations-video-prompt">Video variation prompt</Label>
              <Badge variant={promptQuery.data?.isDefault ? "secondary" : "outline"}>
                {promptQuery.data?.isDefault ? "Shipped default" : "Customized"}
              </Badge>
            </div>
            <Textarea id="creative-iterations-video-prompt" value={text} onChange={(event) => setText(event.target.value)}
              className="min-h-64 resize-y font-mono text-sm" disabled={busy}
              data-testid="textarea-creative-iterations-video-prompt" />
            {!clean && <p className="text-sm text-destructive">Prompt text cannot be blank.</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => save.mutate()} disabled={!dirty || !clean || busy}
                data-testid="button-save-creative-iterations-video-prompt">
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save prompt
              </Button>
              <Button variant="outline" onClick={() => restore.mutate()} disabled={busy || promptQuery.data?.isDefault}
                data-testid="button-restore-creative-iterations-video-prompt">
                {restore.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Restore shipped default
              </Button>
              {dirty && <span className="text-sm text-muted-foreground">Unsaved changes</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
const BRAND_CLIENT_CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "trial_users", label: "Trial Users" },
  { value: "demos", label: "Demos" },
  { value: "current_clients", label: "Current Clients" },
  { value: "smb_automated", label: "SMB" },
  { value: "previous_clients", label: "Previous Clients" },
];

const clientCategoryLabel = (v: string | null | undefined) =>
  BRAND_CLIENT_CATEGORY_OPTIONS.find(o => o.value === v)?.label ?? null;

function BrandCategoryRow({ brand }: { brand: Brand }) {
  const { toast } = useToast();

  const save = useMutation({
    mutationFn: async (clientCategory: string | null) => {
      const res = await apiRequest("PATCH", `/api/admin/brands/${brand.id}/client-category`, { clientCategory });
      return res.json() as Promise<Brand>;
    },
    onSuccess: (_data, clientCategory) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brands"] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brand.id] });
      // Sidebar brand lists use "/api/brands?workspaceId=..." string keys.
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/brands"),
      });
      toast({
        title: "Category updated",
        description: `${brand.name} → ${clientCategoryLabel(clientCategory) ?? "Uncategorized"}`,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update category", description: e.message, variant: "destructive" });
    },
  });

  const current = (brand as any).clientCategory ?? "__none__";

  return (
    <TableRow data-testid={`row-brand-category-${brand.id}`}>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {brand.name}
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
      </TableCell>
      <TableCell>
        {(() => {
          const creator = (brand as any).createdByUser as { name?: string | null; email?: string | null } | null | undefined;
          const label = creator?.name || creator?.email;
          return label ? (
            <span className="text-sm" data-testid={`text-brand-creator-${brand.id}`}>{label}</span>
          ) : (
            <span className="text-xs text-muted-foreground" data-testid={`text-brand-creator-${brand.id}`}>—</span>
          );
        })()}
      </TableCell>
      <TableCell>
        {clientCategoryLabel((brand as any).clientCategory) ? (
          <Badge variant="secondary" data-testid={`badge-current-category-${brand.id}`}>
            {clientCategoryLabel((brand as any).clientCategory)}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid={`badge-current-category-${brand.id}`}>Uncategorized</span>
        )}
      </TableCell>
      <TableCell className="w-56">
        <SearchableSelect
          value={current}
          onValueChange={(v) => {
            const next = v === "__none__" ? null : v;
            if (next === ((brand as any).clientCategory ?? null)) return;
            save.mutate(next);
          }}
          testId={`select-brand-category-${brand.id}`}
          options={[
            { value: "__none__", label: "Uncategorized" },
            ...BRAND_CLIENT_CATEGORY_OPTIONS,
          ]}
        />
      </TableCell>
    </TableRow>
  );
}

type PaginatedBrandsResp = {
  brands: Brand[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalAll: number;
  counts: Record<string, number>;
};

function BrandCategoriesTab() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("__all__");
  const [page, setPage] = useState(1);

  // Debounce name search so typing doesn't refetch per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the search or filter changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryFilter]);

  const { data, isLoading } = useQuery<PaginatedBrandsResp>({
    queryKey: ["/api/admin/brands", { page, search: debouncedSearch, category: categoryFilter }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page) });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (categoryFilter !== "__all__") params.set("category", categoryFilter);
      const res = await apiRequest("GET", `/api/admin/brands?${params.toString()}`);
      return res.json();
    },
    placeholderData: (prev) => prev,
  });

  const brands = data?.brands ?? [];
  const counts = data?.counts ?? {};
  const totalAll = data?.totalAll ?? 0;
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium mb-1" data-testid="text-brand-categories-title">Brands &amp; Client Categories</h2>
        <p className="text-sm text-muted-foreground">
          Set each brand's internal client category (also editable per-brand in the brand wizard).
          Changes save immediately.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search brands by name…"
          className="sm:max-w-xs"
          data-testid="input-brand-category-search"
        />
        <div className="sm:w-64">
          <SearchableSelect
            value={categoryFilter}
            onValueChange={setCategoryFilter}
            testId="select-brand-category-filter"
            options={[
              { value: "__all__", label: `All categories (${totalAll})` },
              { value: "__uncategorized__", label: `Uncategorized (${counts["__uncategorized__"] ?? 0})` },
              ...BRAND_CLIENT_CATEGORY_OPTIONS.map(o => ({
                value: o.value,
                label: `${o.label} (${counts[o.value] ?? 0})`,
              })),
            ]}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : brands.length === 0 ? (
        <div className="rounded-md border p-6 text-sm text-muted-foreground text-center" data-testid="text-no-brands-match">
          {totalAll === 0 ? "No brands found." : "No brands match the current search/filter."}
        </div>
      ) : (
        <>
          <div className="rounded-md border" data-testid="table-brand-categories">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Current category</TableHead>
                  <TableHead className="w-56">Set category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map(b => <BrandCategoryRow key={b.id} brand={b} />)}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground" data-testid="text-brand-page-info">
              Showing {(currentPage - 1) * (data?.pageSize ?? 25) + 1}–{Math.min(currentPage * (data?.pageSize ?? 25), total)} of {total} brand{total === 1 ? "" : "s"}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="button-brand-page-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums" data-testid="text-brand-page-number">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="button-brand-page-next"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StorageTab() {
  const { toast } = useToast();
  const [result, setResult] = useState<UploadsMigrateResult | null>(null);

  const migrate = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/uploads/migrate-to-r2", {});
      return res.json() as Promise<UploadsMigrateResult>;
    },
    onSuccess: (data) => {
      setResult(data);
      const total = Object.values(data || {}).reduce((sum, d) => sum + (d?.migrated || 0), 0);
      toast({ title: "Migration complete", description: `${total} reference(s) migrated to R2.` });
    },
    onError: (e: Error) => {
      toast({ title: "Migration failed", description: e.message, variant: "destructive" });
    },
  });

  const rows: Array<[string, DomainStats | undefined]> = [
    ["Personas", result?.personas],
    ["Ad Units", result?.adUnits],
    ["Brand Logos", result?.brandLogos],
    ["Products", result?.products],
    ["Spy Ads", result?.spyAds],
    ["Generic Assets", result?.assets],
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          Migrate uploads to R2
        </CardTitle>
        <CardDescription>
          Rewrites every <code>/uploads/...</code> reference in the database (personas, ad units,
          brand logos, products, spy ads, generic assets) to a durable R2 URL. Files whose bytes are
          no longer present on disk are reported as <code>missing</code> and left untouched. Run this
          once per environment (dev and prod) after R2 is configured.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          onClick={() => migrate.mutate()}
          disabled={migrate.isPending}
          data-testid="button-migrate-uploads-r2"
        >
          {migrate.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Migrating...
            </>
          ) : (
            <>
              <CloudUpload className="h-4 w-4 mr-2" />
              Migrate uploads to R2
            </>
          )}
        </Button>

        {result && (
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead className="text-right">Refs</TableHead>
                  <TableHead className="text-right">Migrated</TableHead>
                  <TableHead className="text-right">Missing</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(([label, s]) => (
                  <TableRow key={label} data-testid={`row-migration-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <TableCell className="font-medium">{label}</TableCell>
                    <TableCell className="text-right tabular-nums">{s?.totalRefs ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-600 dark:text-green-400">{s?.migrated ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{s?.missing ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400">{s?.failed ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BackfillCharacterBiosCard() {
  const { toast } = useToast();
  const [brandId, setBrandId] = useState<string>("__all__");
  const [lastResult, setLastResult] = useState<{ count: number; brandId: string | null } | null>(null);

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/admin/brands"],
  });

  const backfill = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/backfill-character-bios", {
        brandId: brandId === "__all__" ? undefined : brandId,
      });
      return res.json() as Promise<{ count: number; brandId: string | null; message: string }>;
    },
    onSuccess: (data) => {
      setLastResult({ count: data.count, brandId: data.brandId });
      toast({
        title: "Character-bio backfill started",
        description: `Processing ${data.count} persona${data.count === 1 ? "" : "s"} in the background. Check server logs for progress.`,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to start", description: e.message, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Backfill character bios from avatars
        </CardTitle>
        <CardDescription>
          For every persona that already has an avatar but no primary character,
          this creates a character row from that avatar and runs the Sim
          vision→bio workflow to infer the structured bio (age, gender, look,
          wardrobe, etc.) from the image. Idempotent — personas that already
          have a primary character are skipped. Each persona consumes a Sim
          credit and the job runs in the background.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="charbio-brand-scope">Scope</Label>
            <SearchableSelect
              value={brandId}
              onValueChange={setBrandId}
              testId="select-charbio-brand"
              triggerId="charbio-brand-scope"
              options={[
                { value: "__all__", label: "All brands" },
                ...brands.map(b => ({ value: b.id, label: b.name })),
              ]}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
            data-testid="button-backfill-character-bios"
          >
            {backfill.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working…</>
            ) : (
              <>Backfill missing</>
            )}
          </Button>
        </div>

        {lastResult && (
          <div
            className="text-sm text-muted-foreground border rounded-md p-3 bg-muted/30"
            data-testid="text-charbio-last-result"
          >
            Last kickoff: <span className="font-medium text-foreground">{lastResult.count}</span>{" "}
            persona{lastResult.count === 1 ? "" : "s"} queued
            {lastResult.brandId ? ` (scoped to one brand)` : " (all brands)"}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RegenerateFingerprintsCard() {
  const { toast } = useToast();
  const [brandId, setBrandId] = useState<string>("__all__");
  const [lastResult, setLastResult] = useState<{ count: number; mode: string; brandId: string | null } | null>(null);

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/admin/brands"],
  });

  const regenerate = useMutation({
    mutationFn: async (force: boolean) => {
      const res = await apiRequest("POST", "/api/admin/backfill-persona-fingerprints", {
        force,
        brandId: brandId === "__all__" ? undefined : brandId,
      });
      return res.json() as Promise<{ count: number; mode: string; brandId: string | null; message: string }>;
    },
    onSuccess: (data) => {
      setLastResult({ count: data.count, mode: data.mode, brandId: data.brandId });
      toast({
        title: data.mode === "regenerate-all" ? "Regeneration started" : "Backfill started",
        description: `Processing ${data.count} persona${data.count === 1 ? "" : "s"} in the background. Check server logs for progress.`,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to start", description: e.message, variant: "destructive" });
    },
  });

  const scopeLabel = brandId === "__all__"
    ? "every persona across every brand"
    : `personas in "${brands.find(b => b.id === brandId)?.name ?? brandId}"`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Regenerate persona fingerprints
        </CardTitle>
        <CardDescription>
          Reruns the Sim fingerprint workflow for personas and overwrites the stored
          <code className="mx-1">fingerprint</code> JSON. Use this after the Sim
          fingerprint prompt or schema changes so downstream flows (Generate Characters,
          Character Reference Sheet, Ideate Personas) see the new shape. Each persona
          consumes credits and the job runs in the background (concurrency 3).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="fingerprint-brand-scope">Scope</Label>
            <SearchableSelect
              value={brandId}
              onValueChange={setBrandId}
              testId="select-fingerprint-brand"
              triggerId="fingerprint-brand-scope"
              options={[
                { value: "__all__", label: "All brands" },
                ...brands.map(b => ({ value: b.id, label: b.name })),
              ]}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => regenerate.mutate(false)}
              disabled={regenerate.isPending}
              data-testid="button-backfill-missing-fingerprints"
            >
              {regenerate.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working…</>
              ) : (
                <>Backfill missing only</>
              )}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={regenerate.isPending}
                  data-testid="button-regenerate-all-fingerprints"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate all
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Regenerate every fingerprint?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will rerun the Sim fingerprint workflow for {scopeLabel} and
                    overwrite the existing JSON. This costs credits per persona and
                    cannot be undone. The job runs in the background — check server
                    logs for completion.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-cancel-regenerate-fingerprints">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => regenerate.mutate(true)}
                    data-testid="button-confirm-regenerate-fingerprints"
                  >
                    Yes, regenerate all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {lastResult && (
          <div
            className="text-sm text-muted-foreground border rounded-md p-3 bg-muted/30"
            data-testid="text-fingerprint-last-result"
          >
            Last kickoff: <span className="font-medium text-foreground">{lastResult.count}</span>{" "}
            persona{lastResult.count === 1 ? "" : "s"} queued{" "}
            ({lastResult.mode === "regenerate-all" ? "regenerate-all" : "backfill-missing"}
            {lastResult.brandId ? `, brand ${brands.find(b => b.id === lastResult.brandId)?.name ?? lastResult.brandId}` : ", all brands"}).
            Watch the server console for <code>[Fingerprint] … complete</code>.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UserGroupSelect({ targetUser, allGroups }: { targetUser: AdminUser; allGroups: Group[] }) {
  const { toast } = useToast();
  const value = targetUser.groupId ?? "__none__";

  const setGroupMutation = useMutation({
    mutationFn: async (groupId: string | null) => {
      await apiRequest("PATCH", `/api/admin/users/${targetUser.id}/group`, { groupId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/groups"] });
      toast({ title: "Group updated" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update group", description: e.message, variant: "destructive" });
    },
  });

  return (
    <SearchableSelect
      value={value}
      onValueChange={(v) => setGroupMutation.mutate(v === "__none__" ? null : v)}
      disabled={setGroupMutation.isPending}
      placeholder="No group"
      testId={`select-group-${targetUser.id}`}
      triggerClassName="h-7 text-xs w-[160px]"
      options={[
        { value: "__none__", label: "None", testId: `option-group-none-${targetUser.id}` },
        ...allGroups.map(g => ({ value: g.id, label: g.name, testId: `option-group-${targetUser.id}-${g.id}` })),
      ]}
    />
  );
}

function UserBrandManager({ targetUser, allBrands, allGroups, assignments, assignmentsLoading }: { targetUser: AdminUser; allBrands: Brand[]; allGroups: Group[]; assignments: BrandUser[]; assignmentsLoading: boolean }) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [brandsDialogOpen, setBrandsDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editFirstName, setEditFirstName] = useState(targetUser.firstName || "");
  const [editLastName, setEditLastName] = useState(targetUser.lastName || "");
  const [editEmail, setEditEmail] = useState(targetUser.email || "");
  const [editIsMaster, setEditIsMaster] = useState(targetUser.isMaster);
  const [editIsSuperuser, setEditIsSuperuser] = useState(!!(targetUser as any).isSuperuser);
  const [editCanCreateBrands, setEditCanCreateBrands] = useState(!!(targetUser as any).canCreateBrands);
  const [editCommentNotifications, setEditCommentNotifications] = useState(targetUser.receiveCommentNotifications === true);

  const isLoading = assignmentsLoading;
  const assignedBrandIds = new Set(assignments.map(a => a.brandId));

  const assignMutation = useMutation({
    mutationFn: async (brandId: string) => {
      await apiRequest("POST", `/api/admin/users/${targetUser.id}/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brand-assignments"] });
    },
    onError: () => {
      toast({ title: "Failed to assign brand", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (brandId: string) => {
      await apiRequest("DELETE", `/api/admin/users/${targetUser.id}/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brand-assignments"] });
    },
    onError: () => {
      toast({ title: "Failed to remove brand", variant: "destructive" });
    },
  });

  const passwordMutation = useMutation({
    mutationFn: async (password: string) => {
      await apiRequest("PATCH", `/api/admin/users/${targetUser.id}/password`, { password });
    },
    onSuccess: () => {
      toast({ title: "Password updated" });
      setPasswordDialogOpen(false);
      setNewPassword("");
    },
    onError: (e: Error) => {
      toast({ title: "Failed to set password", description: e.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/admin/users/${targetUser.id}`, {
        firstName: editFirstName || null,
        lastName: editLastName || null,
        email: editEmail,
        isMaster: editIsMaster,
        isSuperuser: editIsSuperuser,
        canCreateBrands: editCanCreateBrands,
        ...(currentUser?.isMaster ? { receiveCommentNotifications: editCommentNotifications } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User updated" });
      setEditDialogOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update user", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/admin/users/${targetUser.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User deactivated" });
      setDeleteDialogOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: "Failed to deactivate user", description: e.message, variant: "destructive" });
    },
  });

  const assignedBrands = allBrands.filter(b => assignedBrandIds.has(b.id));
  const hasPassword = !!(targetUser as any).hasPassword;
  const isSelf = currentUser?.id === targetUser.id;

  return (
    <Card data-testid={`card-user-${targetUser.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate" data-testid={`text-user-name-${targetUser.id}`}>
                {targetUser.firstName || targetUser.lastName
                  ? `${targetUser.firstName || ""} ${targetUser.lastName || ""}`.trim()
                  : targetUser.email || "Unnamed user"}
              </span>
              {targetUser.isMaster && (
                <Badge variant="default" className="text-[10px] px-1.5 py-0" data-testid={`badge-master-${targetUser.id}`}>
                  <Shield className="h-3 w-3 mr-0.5" /> Master
                </Badge>
              )}
              {!targetUser.isMaster && (targetUser as any).isSuperuser && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0" data-testid={`badge-superuser-${targetUser.id}`}>
                  <Shield className="h-3 w-3 mr-0.5" /> Superuser
                </Badge>
              )}
              {hasPassword && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  <KeyRound className="h-3 w-3 mr-0.5" /> Password
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate" data-testid={`text-user-email-${targetUser.id}`}>
              {targetUser.email || "No email"}
            </p>

            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Group:</span>
              <UserGroupSelect targetUser={targetUser} allGroups={allGroups} />
            </div>

            {isLoading ? (
              <div className="mt-2 flex gap-1">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1">
                {targetUser.isMaster && (
                  <Badge variant="secondary" className="text-[10px]">All brands (master)</Badge>
                )}
                {!targetUser.isMaster && assignedBrands.length === 0 && (
                  <span className="text-xs text-muted-foreground">No brand assignments</span>
                )}
                {!targetUser.isMaster && assignedBrands.map(brand => (
                  <Badge key={brand.id} variant="secondary" className="text-[10px] gap-1 pr-1" data-testid={`badge-brand-${targetUser.id}-${brand.id}`}>
                    {brand.name}
                    <button
                      className="hover:text-destructive transition-colors"
                      onClick={() => removeMutation.mutate(brand.id)}
                      disabled={removeMutation.isPending}
                      data-testid={`button-remove-brand-${targetUser.id}-${brand.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Dialog open={editDialogOpen} onOpenChange={(open) => {
              setEditDialogOpen(open);
              if (open) {
                setEditFirstName(targetUser.firstName || "");
                setEditLastName(targetUser.lastName || "");
                setEditEmail(targetUser.email || "");
                setEditIsMaster(targetUser.isMaster);
                setEditIsSuperuser(!!(targetUser as any).isSuperuser);
                setEditCanCreateBrands(!!(targetUser as any).canCreateBrands);
                setEditCommentNotifications(targetUser.receiveCommentNotifications === true);
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit user" data-testid={`button-edit-user-${targetUser.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit {targetUser.firstName || targetUser.email}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor={`edit-first-${targetUser.id}`}>First Name</Label>
                      <Input id={`edit-first-${targetUser.id}`} value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} data-testid="input-edit-first-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`edit-last-${targetUser.id}`}>Last Name</Label>
                      <Input id={`edit-last-${targetUser.id}`} value={editLastName} onChange={(e) => setEditLastName(e.target.value)} data-testid="input-edit-last-name" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`edit-email-${targetUser.id}`}>Email</Label>
                    <Input id={`edit-email-${targetUser.id}`} type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} data-testid="input-edit-email" />
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <div>
                      <Label>Master Admin</Label>
                      <p className="text-xs text-muted-foreground">Full access to all brands and admin features</p>
                    </div>
                    <Switch
                      checked={editIsMaster}
                      onCheckedChange={setEditIsMaster}
                      disabled={isSelf}
                      data-testid="switch-edit-master"
                    />
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <div>
                      <Label>Superuser</Label>
                      <p className="text-xs text-muted-foreground">Can edit, create, archive, and brand-assign ad units (no admin panel access)</p>
                    </div>
                    <Switch
                      checked={editIsSuperuser}
                      onCheckedChange={setEditIsSuperuser}
                      disabled={editIsMaster}
                      data-testid="switch-edit-superuser"
                    />
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <div>
                      <Label>Can Create Brands</Label>
                      <p className="text-xs text-muted-foreground">Can create their own brands (still only sees brands they created, were assigned, or had shared)</p>
                    </div>
                    <Switch
                      checked={editCanCreateBrands}
                      onCheckedChange={setEditCanCreateBrands}
                      disabled={editIsMaster}
                      data-testid="switch-edit-can-create-brands"
                    />
                  </div>
                  {currentUser?.isMaster && (
                    <div className="flex items-center justify-between gap-3 py-1">
                      <Label htmlFor={`edit-comment-notifications-${targetUser.id}`}>Receive notifications for comments</Label>
                      <Switch
                        id={`edit-comment-notifications-${targetUser.id}`}
                        checked={editCommentNotifications}
                        onCheckedChange={setEditCommentNotifications}
                        disabled={editMutation.isPending}
                        data-testid="switch-edit-comment-notifications"
                      />
                    </div>
                  )}
                  <Button
                    onClick={() => editMutation.mutate()}
                    disabled={!editEmail || editMutation.isPending}
                    className="w-full rounded-full"
                    data-testid="button-save-edit"
                  >
                    {editMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={passwordDialogOpen} onOpenChange={(open) => { setPasswordDialogOpen(open); if (!open) { setNewPassword(""); setShowPassword(false); } }}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Set password" data-testid={`button-set-password-${targetUser.id}`}>
                  <KeyRound className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set password for {targetUser.firstName || targetUser.email}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 6 characters"
                        data-testid="input-new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={() => passwordMutation.mutate(newPassword)}
                    disabled={newPassword.length < 6 || passwordMutation.isPending}
                    className="w-full rounded-full"
                    data-testid="button-save-password"
                  >
                    {passwordMutation.isPending ? "Saving..." : "Save Password"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={brandsDialogOpen} onOpenChange={setBrandsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0" data-testid={`button-manage-brands-${targetUser.id}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Brands
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {targetUser.isMaster
                      ? `Brand assignments for ${targetUser.firstName || targetUser.email} (master)`
                      : `Assign brands to ${targetUser.firstName || targetUser.email}`}
                  </DialogTitle>
                </DialogHeader>
                {targetUser.isMaster && (
                  <p className="text-xs text-muted-foreground -mt-1">
                    Master users automatically have access to all brands. Assignments below are for when master status is removed.
                  </p>
                )}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {allBrands.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No brands available</p>
                  ) : (
                    allBrands.map(brand => {
                      const isAssigned = assignedBrandIds.has(brand.id);
                      return (
                        <label
                          key={brand.id}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                          data-testid={`label-brand-toggle-${targetUser.id}-${brand.id}`}
                        >
                          <Checkbox
                            checked={isAssigned}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                assignMutation.mutate(brand.id);
                              } else {
                                removeMutation.mutate(brand.id);
                              }
                            }}
                            disabled={assignMutation.isPending || removeMutation.isPending}
                            data-testid={`checkbox-brand-${targetUser.id}-${brand.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium">{brand.name}</span>
                            {brand.category && (
                              <span className="text-xs text-muted-foreground ml-2">{brand.category}</span>
                            )}
                          </div>
                          {isAssigned && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </label>
                      );
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Deactivate user" data-testid={`button-delete-user-${targetUser.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Deactivate {targetUser.firstName || targetUser.email}?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This blocks their login, ends their active sessions, and removes brand assignments. Their content and usage history are kept, and you can restore the account later from the Deactivated Users list.
                </p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-full">Cancel</Button>
                  <Button
                    variant="destructive"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="rounded-full"
                    data-testid="button-confirm-delete-user"
                  >
                    {deleteMutation.isPending ? "Deactivating..." : "Deactivate User"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AddUserDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/users", {
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        password,
        isMaster,
        isSuperuser,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "User created" });
      onCreated();
      setOpen(false);
      setEmail("");
      setFirstName("");
      setLastName("");
      setPassword("");
      setIsMaster(false);
      setIsSuperuser(false);
    },
    onError: (e: Error) => {
      toast({ title: "Failed to create user", description: e.message, variant: "destructive" });
    },
  });

  const isValid = email.includes("@") && password.length >= 6;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEmail(""); setFirstName(""); setLastName(""); setPassword(""); setIsMaster(false); setIsSuperuser(false); setShowPassword(false); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full gap-2" data-testid="button-add-user">
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              data-testid="input-user-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="user-first-name">First Name</Label>
              <Input
                id="user-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                data-testid="input-user-first-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="user-last-name">Last Name</Label>
              <Input
                id="user-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                data-testid="input-user-last-name"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-password">Password</Label>
            <div className="relative">
              <Input
                id="user-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                data-testid="input-user-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="user-master"
              checked={isMaster}
              onCheckedChange={setIsMaster}
              data-testid="switch-user-master"
            />
            <Label htmlFor="user-master" className="text-sm">Master user (full access to all brands)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="user-superuser"
              checked={isSuperuser}
              onCheckedChange={setIsSuperuser}
              disabled={isMaster}
              data-testid="switch-user-superuser"
            />
            <Label htmlFor="user-superuser" className="text-sm">Superuser (can edit ad units, no admin panel)</Label>
          </div>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!isValid || createMutation.isPending}
            className="w-full rounded-full"
            data-testid="button-create-user"
          >
            {createMutation.isPending ? "Creating..." : "Create User"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdUnitBrandManager({ adUnit, allBrands, allAssignments, allApprovals }: {
  adUnit: AdUnitType;
  allBrands: Brand[];
  allAssignments: BrandAdUnitAccess[];
  allApprovals: BrandAdUnitApproval[];
}) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const unitAssignments = allAssignments.filter(a => a.adUnitTypeId === adUnit.id);
  const isRestricted = unitAssignments.length > 0;
  const assignedBrandIds = new Set(unitAssignments.map(a => a.brandId));

  const assignMutation = useMutation({
    mutationFn: async (brandId: string) => {
      await apiRequest("POST", `/api/admin/ad-units/${adUnit.id}/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units/assignments"] });
    },
    onError: () => {
      toast({ title: "Failed to assign brand", variant: "destructive" });
    },
  });

  const removeBrandMutation = useMutation({
    mutationFn: async (brandId: string) => {
      await apiRequest("DELETE", `/api/admin/ad-units/${adUnit.id}/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units/assignments"] });
    },
    onError: () => {
      toast({ title: "Failed to remove brand", variant: "destructive" });
    },
  });

  const makeUnrestrictedMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/admin/ad-units/${adUnit.id}/assignments`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units/assignments"] });
      toast({ title: "Ad unit is now available to all brands" });
    },
    onError: () => {
      toast({ title: "Failed to update", variant: "destructive" });
    },
  });

  const assignedBrands = allBrands.filter(b => assignedBrandIds.has(b.id));
  return (
    <Card data-testid={`card-adunit-${adUnit.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate" data-testid={`text-adunit-name-${adUnit.id}`}>
                {adUnit.name}
              </span>
              {adUnit.brandId ? (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`badge-owned-${adUnit.id}`}>
                  Brand-owned
                </Badge>
              ) : isRestricted ? (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0" data-testid={`badge-restricted-${adUnit.id}`}>
                  <Lock className="h-3 w-3 mr-0.5" /> Restricted
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" data-testid={`badge-unrestricted-${adUnit.id}`}>
                  <Globe className="h-3 w-3 mr-0.5" /> All brands
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate" data-testid={`text-adunit-slug-${adUnit.id}`}>
              {adUnit.slug} {adUnit.formatType ? `· ${adUnit.formatType}` : ""}
            </p>

            {!adUnit.brandId && isRestricted && (
              <div className="mt-2 flex flex-wrap gap-1">
                {assignedBrands.map(brand => (
                  <Badge key={brand.id} variant="secondary" className="text-[10px] gap-1 pr-1" data-testid={`badge-adunit-brand-${adUnit.id}-${brand.id}`}>
                    {brand.name}
                    <button
                      className="hover:text-destructive transition-colors"
                      onClick={() => removeBrandMutation.mutate(brand.id)}
                      disabled={removeBrandMutation.isPending}
                      data-testid={`button-remove-adunit-brand-${adUnit.id}-${brand.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {!adUnit.brandId && (
            <div className="flex items-center gap-2 shrink-0">
              {isRestricted && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => makeUnrestrictedMutation.mutate()}
                  disabled={makeUnrestrictedMutation.isPending}
                  data-testid={`button-make-unrestricted-${adUnit.id}`}
                >
                  <Globe className="h-3.5 w-3.5 mr-1" /> Unrestrict
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid={`button-manage-adunit-brands-${adUnit.id}`}>
                    <Settings className="h-3.5 w-3.5 mr-1" /> Access &amp; approvals
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Brand access and approval for "{adUnit.name}"</DialogTitle>
                  </DialogHeader>
                  <DialogDescription className="text-xs">
                    {isRestricted
                      ? "This ad unit is restricted to selected brands only."
                      : "This ad unit is currently available to all brands. Selecting any brand will restrict it."}
                    {" "}Access determines eligibility; approval only curates an eligible brand's default Ads Express browser.
                  </DialogDescription>
                  <div className="grid grid-cols-[110px_1fr_auto] gap-3 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <span>Explicit access</span>
                    <span>Brand</span>
                    <span>Approval</span>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {allBrands.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No brands available</p>
                    ) : (
                       allBrands.map(brand => {
                        const isAssigned = assignedBrandIds.has(brand.id);
                        const hasAccess = !isRestricted || isAssigned;
                        return (
                           <div
                            key={brand.id}
                             className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                            data-testid={`label-adunit-brand-toggle-${adUnit.id}-${brand.id}`}
                          >
                             <div className="flex items-center gap-2">
                               <Checkbox
                                 id={`access-${adUnit.id}-${brand.id}`}
                                 checked={isAssigned}
                                 onCheckedChange={(checked) => {
                                   if (checked) {
                                     assignMutation.mutate(brand.id);
                                   } else {
                                     removeBrandMutation.mutate(brand.id);
                                   }
                                 }}
                                 disabled={assignMutation.isPending || removeBrandMutation.isPending}
                                 data-testid={`checkbox-adunit-brand-${adUnit.id}-${brand.id}`}
                               />
                               <Label htmlFor={`access-${adUnit.id}-${brand.id}`} className="text-xs text-muted-foreground">
                                 Grant
                               </Label>
                             </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium">{brand.name}</span>
                              {brand.category && (
                                <span className="text-xs text-muted-foreground ml-2">{brand.category}</span>
                              )}
                               <p className={`text-[10px] ${hasAccess ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>
                                 {hasAccess
                                   ? isRestricted
                                     ? "Can access this template"
                                     : "Can access via the all-brands default"
                                   : "Restricted from this template"}
                               </p>
                            </div>
                             <AdUnitApprovalControl
                               adUnit={adUnit}
                               brand={brand}
                                hasAccess={hasAccess}
                               allApprovals={allApprovals}
                             />
                           </div>
                        );
                      })
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdUnitApprovalControl({
  adUnit,
  brand,
  hasAccess,
  allApprovals,
}: {
  adUnit: AdUnitType;
  brand: Brand;
  hasAccess: boolean;
  allApprovals: BrandAdUnitApproval[];
}) {
  const { toast } = useToast();
  const approved = allApprovals.some(
    (item) => item.adUnitTypeId === adUnit.id && item.brandId === brand.id,
  );
  const refreshApprovals = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units/approvals"] });

  const approvalMutation = useMutation({
    mutationFn: async (nextApproved: boolean) => {
      if (nextApproved === approved) return;
      const endpoint = `/api/admin/ad-units/${adUnit.id}/brands/${brand.id}/approval`;
      await apiRequest(nextApproved ? "POST" : "DELETE", endpoint);
    },
    onSuccess: refreshApprovals,
    onError: (error: Error) => toast({
      title: "Failed to update template approval",
      description: error.message,
      variant: "destructive",
    }),
  });

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <Label htmlFor={`approval-${adUnit.id}-${brand.id}`} className="text-xs text-muted-foreground">
        Approved
      </Label>
      <Switch
        id={`approval-${adUnit.id}-${brand.id}`}
        checked={approved}
        onCheckedChange={(checked) => approvalMutation.mutate(checked)}
        disabled={!hasAccess || approvalMutation.isPending}
        data-testid={`switch-adunit-approval-${adUnit.id}-${brand.id}`}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={refreshApprovals}
        aria-label={`Refresh ${brand.name} template approval`}
        data-testid={`button-refresh-adunit-approval-${adUnit.id}-${brand.id}`}
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function ApproveAccessibleAdUnitsForBrand({
  allBrands,
  allAdUnits,
  allAssignments,
  allApprovals,
}: {
  allBrands: Brand[];
  allAdUnits: AdUnitType[];
  allAssignments: BrandAdUnitAccess[];
  allApprovals: BrandAdUnitApproval[];
}) {
  const { toast } = useToast();
  const [brandId, setBrandId] = useState("");
  const brand = allBrands.find((candidate) => candidate.id === brandId);
  const restrictedIds = useMemo(
    () => new Set(allAssignments.map((assignment) => assignment.adUnitTypeId)),
    [allAssignments],
  );
  const assignedIds = useMemo(
    () => new Set(
      allAssignments
        .filter((assignment) => assignment.brandId === brandId)
        .map((assignment) => assignment.adUnitTypeId),
    ),
    [allAssignments, brandId],
  );
  const accessibleIds = useMemo(
    () => new Set(
      allAdUnits
        .filter((unit) => !restrictedIds.has(unit.id) || assignedIds.has(unit.id))
        .map((unit) => unit.id),
    ),
    [allAdUnits, restrictedIds, assignedIds],
  );
  const approvedCount = allApprovals.filter(
    (approval) => approval.brandId === brandId && accessibleIds.has(approval.adUnitTypeId),
  ).length;

  const mutation = useMutation({
    mutationFn: async () => {
      const expectedCount = accessibleIds.size;
      const res = await apiRequest(
        "POST",
        `/api/admin/brands/${brandId}/ad-units/approve-accessible`,
      );
      const rows = (await res.json()) as BrandAdUnitApproval[];
      return { expectedCount, resultingCount: rows.length };
    },
    onSuccess: ({ expectedCount, resultingCount }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units/approvals"] });
      if (resultingCount < expectedCount) {
        toast({
          title: "Some templates could not be approved",
          description: `${resultingCount} of ${expectedCount} accessible templates are approved. You can retry safely.`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Accessible templates approved",
        description: `${resultingCount} template${resultingCount === 1 ? "" : "s"} approved for ${brand?.name ?? "this brand"}.`,
      });
    },
    onError: (error: Error) => toast({
      title: "Could not approve accessible templates",
      description: error.message,
      variant: "destructive",
    }),
  });

  return (
    <div className="rounded-lg border bg-muted/20 p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-medium">Approve a brand's accessible library</p>
        <p className="text-xs text-muted-foreground">
          Adds approvals only for global templates the brand can already access.
          To curate individual templates, use Access &amp; approvals on a global ad unit below.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Select value={brandId} onValueChange={setBrandId}>
          <SelectTrigger className="w-[220px]" data-testid="select-approve-all-brand">
            <SelectValue placeholder="Choose a brand" />
          </SelectTrigger>
          <SelectContent>
            {allBrands.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          onClick={() => mutation.mutate()}
          disabled={!brandId || mutation.isPending || accessibleIds.size === approvedCount}
          data-testid="button-approve-all-accessible"
        >
          {mutation.isPending
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Approving…</>
            : brandId && accessibleIds.size === approvedCount
              ? "All approved"
              : `Approve all${brandId ? ` (${accessibleIds.size - approvedCount})` : ""}`}
        </Button>
      </div>
    </div>
  );
}

function PendingApprovals({ users, isLoading }: { users: User[]; isLoading: boolean }) {
  const { toast } = useToast();
  // Rejected signups are deactivated (deletedAt stamped) but keep
  // isApproved=false — exclude them so they don't reappear as pending.
  const pendingUsers = users.filter(u => u.isApproved === false && !u.deletedAt);

  const approveMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("PATCH", `/api/admin/users/${userId}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User approved" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to approve", description: e.message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("PATCH", `/api/admin/users/${userId}/reject`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User rejected" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to reject", description: e.message, variant: "destructive" });
    },
  });

  if (isLoading || pendingUsers.length === 0) return null;

  return (
    <div data-testid="section-pending-approvals">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-amber-500" />
        <h2 className="text-lg font-medium">Pending Approvals</h2>
        <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
          {pendingUsers.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {pendingUsers.map(u => (
          <Card key={u.id} className="border-amber-200/50 bg-amber-50/30 dark:border-amber-800/30 dark:bg-amber-950/20" data-testid={`card-pending-user-${u.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm" data-testid={`text-pending-name-${u.id}`}>
                      {u.firstName || u.lastName ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : "No name provided"}
                    </span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-600">
                      <Clock className="h-3 w-3 mr-0.5" /> Pending
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid={`text-pending-email-${u.id}`}>
                    {u.email}
                  </p>
                  {u.createdAt && (
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      Requested {new Date(u.createdAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:hover:bg-red-950"
                    onClick={() => rejectMutation.mutate(u.id)}
                    disabled={rejectMutation.isPending || approveMutation.isPending}
                    data-testid={`button-reject-${u.id}`}
                  >
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    className="rounded-full gap-1.5"
                    onClick={() => approveMutation.mutate(u.id)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                    data-testid={`button-approve-${u.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function DeactivatedUsers({ users }: { users: AdminUser[] }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const deactivated = users.filter(u => !!u.deletedAt);

  const restoreMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", `/api/admin/users/${userId}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User restored", description: "They can log in again." });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to restore user", description: e.message, variant: "destructive" });
    },
  });

  if (deactivated.length === 0) return null;

  return (
    <div data-testid="section-deactivated-users">
      <button
        type="button"
        className="flex items-center gap-2 mb-3 text-left"
        onClick={() => setExpanded(v => !v)}
        data-testid="button-toggle-deactivated-users"
      >
        <h2 className="text-lg font-medium text-muted-foreground">Deactivated Users</h2>
        <Badge variant="secondary" className="text-[10px]">{deactivated.length}</Badge>
        <span className="text-xs text-muted-foreground underline">{expanded ? "Hide" : "Show"}</span>
      </button>
      {expanded && (
        <div className="space-y-2">
          {deactivated.map(u => (
            <Card key={u.id} className="opacity-80" data-testid={`card-deactivated-user-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-sm" data-testid={`text-deactivated-name-${u.id}`}>
                      {u.firstName || u.lastName ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : (u.email || "No name")}
                    </span>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                    {u.deletedAt && (
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                        Deactivated {new Date(u.deletedAt as any).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full shrink-0"
                    onClick={() => restoreMutation.mutate(u.id)}
                    disabled={restoreMutation.isPending}
                    data-testid={`button-restore-user-${u.id}`}
                  >
                    Restore
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

type ModelPricingRow = {
  id: string;
  modelName: string;
  provider: string;
  sourceUrl: string | null;
  inputPer1m: number | null;
  outputPer1m: number | null;
  lastUpdated: string | null;
  createdAt: string | null;
};

function InlineEditableValue({
  value,
  suffix,
  onSave,
  isSaving,
  min,
  step,
  testId,
  inputTestId,
}: {
  value: number;
  suffix?: string;
  onSave: (val: number) => void;
  isSaving: boolean;
  min: number;
  step: string;
  testId: string;
  inputTestId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(String(value));
    setEditing(false);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (isNaN(parsed) || parsed < min) {
      setDraft(String(value));
      setEditing(false);
      return;
    }
    if (parsed !== value) {
      onSave(parsed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          type="number"
          min={min}
          step={step}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          className="h-8 w-28 text-xl font-bold tabular-nums px-2"
          data-testid={inputTestId}
        />
        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  return (
    <p
      className="text-2xl font-bold tabular-nums cursor-pointer hover:text-primary transition-colors rounded px-1 -mx-1 hover:bg-primary/5"
      onClick={() => setEditing(true)}
      title="Click to edit"
      data-testid={testId}
    >
      {value.toLocaleString()}{suffix}
    </p>
  );
}

function AddModelDialog({ onAdd }: { onAdd: () => void }) {
  const [open, setOpen] = useState(false);
  const [modelName, setModelName] = useState("");
  const [provider, setProvider] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const { toast } = useToast();

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/model-pricing", {
        modelName, provider,
        sourceUrl: sourceUrl || null,
      });
      return res.json();
    },
    onSuccess: () => {
      onAdd();
      setOpen(false);
      setModelName(""); setProvider(""); setSourceUrl("");
      toast({ title: "Model added", description: `${modelName} has been added to pricing.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-add-model">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Model
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Model to Pricing</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Model Name</Label>
            <Input value={modelName} onChange={e => setModelName(e.target.value)} placeholder="e.g. GPT-4o" data-testid="input-model-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Provider</Label>
            <Input value={provider} onChange={e => setProvider(e.target.value)} placeholder="e.g. OpenAI" data-testid="input-model-provider" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Pricing Source URL</Label>
            <Input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="e.g. https://openai.com/api/pricing/" data-testid="input-model-source-url" />
            <p className="text-[10px] text-muted-foreground">The page where pricing for this model is listed. Used for automatic price updates.</p>
          </div>
          <Button
            className="w-full"
            onClick={() => addMutation.mutate()}
            disabled={!modelName || !provider || addMutation.isPending}
            data-testid="button-confirm-add-model"
          >
            {addMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Model
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type UnitPricingRow = {
  id: string;
  modelName: string;
  provider: string;
  sourceUrl: string | null;
  unitType: string;
  pricingTiers: Record<string, number> | null;
  lastUpdated: string | null;
  createdAt: string | null;
};

function AddUnitModelDialog({ onAdd }: { onAdd: () => void }) {
  const [open, setOpen] = useState(false);
  const [modelName, setModelName] = useState("");
  const [provider, setProvider] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [unitType, setUnitType] = useState("image");
  const { toast } = useToast();

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/unit-pricing", {
        modelName, provider,
        sourceUrl: sourceUrl || null,
        unitType,
      });
      return res.json();
    },
    onSuccess: () => {
      onAdd();
      setOpen(false);
      setModelName(""); setProvider(""); setSourceUrl(""); setUnitType("image");
      toast({ title: "Model added", description: `${modelName} has been added to unit pricing.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-add-unit-model">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Model
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Model to Unit Pricing</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Model Name</Label>
            <Input value={modelName} onChange={e => setModelName(e.target.value)} placeholder="e.g. nano-banana-2" data-testid="input-unit-model-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Provider</Label>
            <Input value={provider} onChange={e => setProvider(e.target.value)} placeholder="e.g. fal.ai" data-testid="input-unit-model-provider" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Unit Type</Label>
            <Input value={unitType} onChange={e => setUnitType(e.target.value)} placeholder="e.g. image, video" data-testid="input-unit-type" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Pricing Source URL</Label>
            <Input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="e.g. https://fal.ai/pricing" data-testid="input-unit-source-url" />
            <p className="text-[10px] text-muted-foreground">The page where pricing for this model is listed. Used for automatic price updates.</p>
          </div>
          <Button
            className="w-full"
            onClick={() => addMutation.mutate()}
            disabled={!modelName || !provider || addMutation.isPending}
            data-testid="button-confirm-add-unit-model"
          >
            {addMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Model
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModelSettingsTab() {
  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [editModelData, setEditModelData] = useState<{ sourceUrl: string }>({ sourceUrl: "" });
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editUnitData, setEditUnitData] = useState<{ sourceUrl: string }>({ sourceUrl: "" });
  const { toast } = useToast();

  const { data: pricingModels = [], isLoading: pricingLoading } = useQuery<ModelPricingRow[]>({
    queryKey: ["/api/admin/model-pricing"],
  });

  const { data: unitModels = [], isLoading: unitPricingLoading } = useQuery<UnitPricingRow[]>({
    queryKey: ["/api/admin/unit-pricing"],
  });

  const updateModelMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/model-pricing/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/model-pricing"] });
      setEditingModelId(null);
      toast({ title: "Model updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/model-pricing/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/model-pricing"] });
      toast({ title: "Model removed" });
    },
  });

  const refreshAllMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/model-pricing/refresh");
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/model-pricing"] });
      toast({ title: "Price refresh complete", description: `${data.succeeded}/${data.total} models updated successfully.` });
    },
    onError: (err: Error) => {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    },
  });

  const refreshSingleMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/model-pricing/${id}/refresh`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/model-pricing"] });
      toast({ title: "Price updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    },
  });

  const updateUnitMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/unit-pricing/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-pricing"] });
      setEditingUnitId(null);
      toast({ title: "Model updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteUnitMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/unit-pricing/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-pricing"] });
      toast({ title: "Model removed" });
    },
  });

  const refreshAllUnitMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/unit-pricing/refresh");
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-pricing"] });
      toast({ title: "Unit price refresh complete", description: `${data.succeeded}/${data.total} models updated successfully.` });
    },
    onError: (err: Error) => {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    },
  });

  const refreshSingleUnitMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/unit-pricing/${id}/refresh`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-pricing"] });
      toast({ title: "Price updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    },
  });

  const startEditModel = (model: ModelPricingRow) => {
    setEditingModelId(model.id);
    setEditModelData({ sourceUrl: model.sourceUrl || "" });
  };

  const saveEditModel = () => {
    if (!editingModelId) return;
    updateModelMutation.mutate({
      id: editingModelId,
      data: { sourceUrl: editModelData.sourceUrl || null },
    });
  };

  const startEditUnit = (model: UnitPricingRow) => {
    setEditingUnitId(model.id);
    setEditUnitData({ sourceUrl: model.sourceUrl || "" });
  };

  const saveEditUnit = () => {
    if (!editingUnitId) return;
    updateUnitMutation.mutate({
      id: editingUnitId,
      data: { sourceUrl: editUnitData.sourceUrl || null },
    });
  };

  const allUnitTierKeys = Array.from(new Set(unitModels.flatMap(m => m.pricingTiers ? Object.keys(m.pricingTiers) : [])));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                <TrendingUp className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Model Token Pricing</CardTitle>
                <CardDescription className="text-xs">Manage models and auto-update pricing from provider pages</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshAllMutation.mutate()}
                disabled={refreshAllMutation.isPending || pricingModels.length === 0}
                data-testid="button-refresh-all-pricing"
              >
                {refreshAllMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Refresh All Prices
              </Button>
              <AddModelDialog onAdd={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/model-pricing"] })} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {pricingLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : pricingModels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <TrendingUp className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No models configured yet. Add a model to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="th-model">Model</TableHead>
                  <TableHead data-testid="th-provider">Provider</TableHead>
                  <TableHead data-testid="th-source">Source</TableHead>
                  <TableHead className="text-right" data-testid="th-input">Input / 1M tokens</TableHead>
                  <TableHead className="text-right" data-testid="th-output">Output / 1M tokens</TableHead>
                  <TableHead className="text-right" data-testid="th-updated">Updated</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingModels.map((row) => (
                  <TableRow key={row.id} data-testid={`row-model-${row.modelName.toLowerCase().replace(/\s+/g, "-")}`}>
                    <TableCell className="font-medium text-sm">{row.modelName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {row.provider}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {editingModelId === row.id ? (
                        <Input
                          value={editModelData.sourceUrl}
                          onChange={e => setEditModelData(d => ({ ...d, sourceUrl: e.target.value }))}
                          placeholder="https://..."
                          className="h-7 text-xs w-48"
                          data-testid={`input-source-url-${row.id}`}
                        />
                      ) : row.sourceUrl ? (
                        <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1" title={row.sourceUrl}>
                          <Link2 className="h-3 w-3" />
                          <span className="max-w-[120px] truncate">{new URL(row.sourceUrl).hostname}</span>
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.inputPer1m !== null ? `$${row.inputPer1m.toFixed(2)}` : <span className="text-muted-foreground text-xs">N/A</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.outputPer1m !== null ? `$${row.outputPer1m.toFixed(2)}` : <span className="text-muted-foreground text-xs">N/A</span>}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                      {row.lastUpdated ? new Date(row.lastUpdated).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editingModelId === row.id ? (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={saveEditModel} disabled={updateModelMutation.isPending} data-testid={`button-save-model-${row.id}`}>
                              {updateModelMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingModelId(null)} data-testid={`button-cancel-model-${row.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => refreshSingleMutation.mutate(row.id)}
                              disabled={!row.sourceUrl || refreshSingleMutation.isPending}
                              title="Refresh price"
                              data-testid={`button-refresh-model-${row.id}`}
                            >
                              {refreshSingleMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditModel(row)} title="Edit" data-testid={`button-edit-model-${row.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteModelMutation.mutate(row.id)} title="Delete" data-testid={`button-delete-model-${row.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                <ImageIcon className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Static Unit Pricing</CardTitle>
                <CardDescription className="text-xs">Per-image/video pricing by resolution for generation models</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshAllUnitMutation.mutate()}
                disabled={refreshAllUnitMutation.isPending || unitModels.length === 0}
                data-testid="button-refresh-all-unit-pricing"
              >
                {refreshAllUnitMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Refresh All Prices
              </Button>
              <AddUnitModelDialog onAdd={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-pricing"] })} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {unitPricingLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : unitModels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <ImageIcon className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No unit-priced models configured yet. Add a model to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="th-unit-model">Model</TableHead>
                  <TableHead data-testid="th-unit-provider">Provider</TableHead>
                  <TableHead data-testid="th-unit-type">Type</TableHead>
                  <TableHead data-testid="th-unit-source">Source</TableHead>
                  {allUnitTierKeys.map(tier => (
                    <TableHead key={tier} className="text-right" data-testid={`th-tier-${tier}`}>{tier}</TableHead>
                  ))}
                  <TableHead className="text-right" data-testid="th-unit-updated">Updated</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unitModels.map((row) => (
                  <TableRow key={row.id} data-testid={`row-unit-${row.modelName.toLowerCase().replace(/\s+/g, "-")}`}>
                    <TableCell className="font-medium text-sm">{row.modelName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-normal">{row.provider}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] font-normal">{row.unitType}</Badge>
                    </TableCell>
                    <TableCell>
                      {editingUnitId === row.id ? (
                        <Input
                          value={editUnitData.sourceUrl}
                          onChange={e => setEditUnitData(d => ({ ...d, sourceUrl: e.target.value }))}
                          placeholder="https://..."
                          className="h-7 text-xs w-48"
                          data-testid={`input-unit-source-url-${row.id}`}
                        />
                      ) : row.sourceUrl ? (
                        <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1" title={row.sourceUrl}>
                          <Link2 className="h-3 w-3" />
                          <span className="max-w-[120px] truncate">{new URL(row.sourceUrl).hostname}</span>
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    {allUnitTierKeys.map(tier => (
                      <TableCell key={tier} className="text-right text-sm tabular-nums">
                        {row.pricingTiers && row.pricingTiers[tier] !== undefined
                          ? `$${row.pricingTiers[tier].toFixed(4)}`
                          : <span className="text-muted-foreground text-xs">—</span>
                        }
                      </TableCell>
                    ))}
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                      {row.lastUpdated ? new Date(row.lastUpdated).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editingUnitId === row.id ? (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={saveEditUnit} disabled={updateUnitMutation.isPending} data-testid={`button-save-unit-${row.id}`}>
                              {updateUnitMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingUnitId(null)} data-testid={`button-cancel-unit-${row.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => refreshSingleUnitMutation.mutate(row.id)}
                              disabled={!row.sourceUrl || refreshSingleUnitMutation.isPending}
                              title="Refresh price"
                              data-testid={`button-refresh-unit-${row.id}`}
                            >
                              {refreshSingleUnitMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditUnit(row)} title="Edit" data-testid={`button-edit-unit-${row.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteUnitMutation.mutate(row.id)} title="Delete" data-testid={`button-delete-unit-${row.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserCreditsTab({ allUsers }: { allUsers: User[] }) {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const { toast } = useToast();

  const { data: userCreditsData, isLoading: creditsLoading } = useQuery<{ credits: number; balance: number; multiplier: number }>({
    queryKey: ["/api/admin/users", selectedUserId, "credits"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/users/${selectedUserId}/credits`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch credits");
      return res.json();
    },
    enabled: !!selectedUserId,
  });

  const { data: marginData } = useQuery<{ simCostUsd: number; apiCostUsd: number; unitCostUsd: number; unitCount: number; totalCostUsd: number; adjustedCostUsd: number; marginUsd: number; marginPct: number; executionCount: number; unknownSupplierCount?: number }>({
    queryKey: ["/api/admin/users", selectedUserId, "margin"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/users/${selectedUserId}/margin`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch margin");
      return res.json();
    },
    enabled: !!selectedUserId,
  });

  const creditSaveMutation = useMutation({
    mutationFn: async ({ credits, balance, multiplier }: { credits: number; balance: number; multiplier: number }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${selectedUserId}/credits`, { credits, balance, multiplier });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", selectedUserId, "credits"] });
      toast({ title: "Credits updated", description: "User credit settings have been saved." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleSaveCredits = (newCredits: number) => {
    if (!userCreditsData) return;
    creditSaveMutation.mutate({ credits: newCredits, balance: userCreditsData.balance, multiplier: userCreditsData.multiplier });
  };

  const handleSaveBalance = (newBalance: number) => {
    if (!userCreditsData) return;
    creditSaveMutation.mutate({ credits: userCreditsData.credits, balance: newBalance, multiplier: userCreditsData.multiplier });
  };

  const handleSaveMultiplier = (newMultiplier: number) => {
    if (!userCreditsData) return;
    creditSaveMutation.mutate({ credits: userCreditsData.credits, balance: userCreditsData.balance, multiplier: newMultiplier });
  };

  const resetCreditsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/users/${selectedUserId}/credits/reset`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", selectedUserId, "credits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", selectedUserId, "margin"] });
      toast({ title: "Credits reset", description: "Remaining credits restored to match total balance. A reset log entry has been recorded." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetMarginMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/users/${selectedUserId}/margin/reset`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", selectedUserId, "margin"] });
      queryClient.invalidateQueries({ queryKey: ["/api/me/usage-logs"] });
      toast({ title: "Margin reset", description: "Margin analysis zeroed out. A reset record has been saved for auditing." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleResetMargin = () => {
    if (!selectedUserId) return;
    const ok = window.confirm(
      "Reset margin analysis for this user?\n\nThis will zero out all cost and margin values by inserting an offsetting record. All previous entries are preserved for auditing."
    );
    if (!ok) return;
    resetMarginMutation.mutate();
  };

  const handleResetCredits = () => {
    if (!userCreditsData || !selectedUserId) return;
    const spent = userCreditsData.balance - userCreditsData.credits;
    const ok = window.confirm(
      `Reset remaining credits to match total balance (${userCreditsData.balance})?\n\nThis will restore ${spent.toFixed(2)} credits and set spent to $0.00.\n\nA reset record will be saved so this can be undone if needed.`
    );
    if (!ok) return;
    resetCreditsMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <Coins className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">User Credit Balances</CardTitle>
              <CardDescription className="text-xs">Manage credits and charge multipliers per user</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="max-w-sm">
            <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Select User</Label>
            <SearchableSelect
              value={selectedUserId}
              onValueChange={setSelectedUserId}
              placeholder="Choose a user..."
              testId="select-credit-user"
              options={allUsers.filter(u => u.isApproved !== false).map(u => ({
                value: u.id,
                label: (u.firstName || u.lastName ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : u.email) ?? "",
                testId: `select-user-${u.id}`,
                node: (
                  <span className="flex items-center gap-2">
                    {u.firstName || u.lastName ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : u.email}
                    {u.isMaster && <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1">Admin</Badge>}
                  </span>
                ),
              }))}
            />
          </div>

          {selectedUserId && creditsLoading && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          )}

          {selectedUserId && userCreditsData && !creditsLoading && (
            <>
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetCredits}
                  disabled={creditSaveMutation.isPending || resetCreditsMutation.isPending || userCreditsData.credits === userCreditsData.balance}
                  data-testid="button-reset-credits"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Reset Credits
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4" data-testid="section-user-credits">
                <Card className="bg-muted/30 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 shrink-0">
                        <Coins className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <InlineEditableValue
                          value={userCreditsData.credits}
                          onSave={handleSaveCredits}
                          isSaving={creditSaveMutation.isPending}
                          min={0}
                          step="1"
                          testId="text-credit-remaining"
                          inputTestId="input-edit-credits"
                        />
                        <p className="text-xs text-muted-foreground">Remaining Credits</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-muted/30 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 shrink-0">
                        <Coins className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <InlineEditableValue
                          value={userCreditsData.balance}
                          onSave={handleSaveBalance}
                          isSaving={creditSaveMutation.isPending}
                          min={0}
                          step="1"
                          testId="text-credit-balance"
                          inputTestId="input-edit-balance"
                        />
                        <p className="text-xs text-muted-foreground">Total Balance</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-muted/30 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 shrink-0">
                        <Gauge className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <InlineEditableValue
                          value={userCreditsData.multiplier}
                          suffix="x"
                          onSave={handleSaveMultiplier}
                          isSaving={creditSaveMutation.isPending}
                          min={0.1}
                          step="0.1"
                          testId="text-credit-multiplier"
                          inputTestId="input-edit-multiplier"
                        />
                        <p className="text-xs text-muted-foreground">Charge Multiplier</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="rounded-xl border border-border/40 bg-card mt-4 p-4" data-testid="section-dollar-equivalency">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Dollar Equivalency</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-lg font-semibold tabular-nums" data-testid="text-dollar-remaining">
                      ${(userCreditsData.credits / 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted-foreground">Remaining Value</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold tabular-nums" data-testid="text-dollar-balance">
                      ${(userCreditsData.balance / 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted-foreground">Total Value</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold tabular-nums" data-testid="text-dollar-spent">
                      ${((userCreditsData.balance - userCreditsData.credits) / 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted-foreground">Total Spent</p>
                  </div>
                </div>
              </div>

              {marginData && (
                <div className="rounded-xl border border-border/40 bg-card mt-4 p-4" data-testid="section-user-margin">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Margin Analysis</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px] text-muted-foreground hover:text-destructive"
                      onClick={handleResetMargin}
                      disabled={resetMarginMutation.isPending || marginData.executionCount === 0}
                      data-testid="button-reset-margin"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Reset
                    </Button>
                  </div>
                  <div className="grid grid-cols-7 gap-4">
                    <div>
                      <p className="text-lg font-semibold tabular-nums" data-testid="text-margin-sim-cost">
                        ${marginData.simCostUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </p>
                      <p className="text-xs text-muted-foreground">Sim Cost</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold tabular-nums" data-testid="text-margin-api-cost">
                        ${(marginData.apiCostUsd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </p>
                      <p className="text-xs text-muted-foreground">API Cost</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold tabular-nums" data-testid="text-margin-unit-cost">
                        ${(marginData.unitCostUsd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </p>
                      <p className="text-xs text-muted-foreground">Unit Cost ({marginData.unitCount ?? 0})</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold tabular-nums" data-testid="text-margin-total-cost">
                        ${marginData.totalCostUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </p>
                      <p className="text-xs text-muted-foreground">{marginData.unknownSupplierCount ? `Known costs only; ${marginData.unknownSupplierCount} supplier costs unknown` : "Total Cost"}</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold tabular-nums" data-testid="text-margin-charged">
                        ${marginData.adjustedCostUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </p>
                      <p className="text-xs text-muted-foreground">Charged to User</p>
                    </div>
                    <div>
                      <p className={`text-lg font-semibold tabular-nums ${marginData.marginUsd >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`} data-testid="text-margin-dollars">
                        {marginData.unknownSupplierCount ? "Unknown" : `$${marginData.marginUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`}
                      </p>
                      <p className="text-xs text-muted-foreground">Margin ($)</p>
                    </div>
                    <div>
                      <p className={`text-lg font-semibold tabular-nums ${marginData.marginPct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`} data-testid="text-margin-pct">
                        {marginData.unknownSupplierCount ? "Unknown" : `${marginData.marginPct.toFixed(1)}%`}
                      </p>
                      <p className="text-xs text-muted-foreground">Margin (%) · {marginData.executionCount} runs</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!selectedUserId && (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <Users className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">Select a user above to view their credit balance and multiplier</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type GroupRow = Group & { memberCount?: number };

function GroupsTab() {
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: groups = [], isLoading } = useQuery<GroupRow[]>({
    queryKey: ["/api/admin/groups"],
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/admin/groups", { name: newName.trim() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setNewName("");
      toast({ title: "Group created" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to create group", description: e.message, variant: "destructive" });
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiRequest("PATCH", `/api/admin/groups/${id}`, { name: name.trim() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setEditingId(null);
      setEditingName("");
      toast({ title: "Group renamed" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to rename group", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/groups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setDeleteId(null);
      toast({ title: "Group deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to delete group", description: e.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6" data-testid="section-groups">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create Group</CardTitle>
          <CardDescription>Groups represent the client/company a user belongs to. Users can only share resources with other users in the same group.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Group name (e.g. Gaffle Coffee)"
              data-testid="input-new-group-name"
              onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) createMutation.mutate(); }}
            />
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
              className="rounded-full gap-1.5"
              data-testid="button-create-group"
            >
              <Plus className="h-4 w-4" />
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-medium mb-3">Groups</h2>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : groups.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="text-no-groups">
              No groups yet. Create the first one above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {groups.map(g => (
              <Card key={g.id} data-testid={`card-group-${g.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {editingId === g.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="h-8 text-sm max-w-xs"
                            autoFocus
                            data-testid={`input-rename-group-${g.id}`}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && editingName.trim()) {
                                renameMutation.mutate({ id: g.id, name: editingName });
                              } else if (e.key === "Escape") {
                                setEditingId(null);
                                setEditingName("");
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={() => renameMutation.mutate({ id: g.id, name: editingName })}
                            disabled={!editingName.trim() || renameMutation.isPending}
                            data-testid={`button-save-rename-${g.id}`}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingId(null); setEditingName(""); }}
                            data-testid={`button-cancel-rename-${g.id}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-sm" data-testid={`text-group-name-${g.id}`}>{g.name}</p>
                          <p className="text-xs text-muted-foreground" data-testid={`text-group-members-${g.id}`}>
                            {g.memberCount ?? 0} {(g.memberCount ?? 0) === 1 ? "member" : "members"}
                          </p>
                        </>
                      )}
                    </div>
                    {editingId !== g.id && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="Rename group"
                          onClick={() => { setEditingId(g.id); setEditingName(g.name); }}
                          data-testid={`button-edit-group-${g.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          title="Delete group"
                          onClick={() => setDeleteId(g.id)}
                          data-testid={`button-delete-group-${g.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <GroupMembersList groupId={g.id} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this group?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Members of this group will be unassigned (they will have no group). This will not delete any users. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="rounded-full">Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              className="rounded-full"
              data-testid="button-confirm-delete-group"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Group"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupMembersList({ groupId }: { groupId: string }) {
  const { data: members = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/groups", groupId, "users"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/groups/${groupId}/users`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch members");
      return res.json();
    },
  });

  if (isLoading) {
    return <Skeleton className="h-4 w-32 mt-2" />;
  }
  if (members.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`group-members-${groupId}`}>
      {members.map(m => (
        <Badge key={m.id} variant="secondary" className="text-[10px]" data-testid={`group-member-${groupId}-${m.id}`}>
          {m.firstName || m.lastName ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : m.email}
        </Badge>
      ))}
    </div>
  );
}

const BRAND_FEATURE_FLAGS = [
  { key: "enableLearnings", label: "Learnings", description: "Show the Learnings section in the sidebar" },
  // The legacy "Landing Pages" toggle (enableLandingPages) is intentionally
  // hidden for now — the stored setting values are preserved so it can be
  // restored later. Landing Pages Express replaces it in this tab.
  { key: "enableLandingPagesExpress", label: "Landing Pages Express", description: "Let non-master users of this brand use Landing Pages Express" },
  { key: "enableUgcExpress", label: "UGC Express", description: "Let non-master users of this brand use UGC Express" },
  { key: "enableBrollExpress", label: "B-roll Express", description: "Let non-master users of this brand use B-roll Express" },
  { key: "enableVideoExpressMax", label: "Video Express Max", description: "Let non-master users of this brand use Video Express Max" },
  // Task #2375 — Creative Strategist. Authoritative for EVERYONE (masters
  // included): off = the feature does not exist for this brand anywhere.
  { key: "enableCreativeStrategist", label: "Creative Strategist", description: "Give this brand the AI project manager that orchestrates the existing tools" },
  // "Ad Launcher" (enableAdLauncher) moved to the Analytics tab's per-brand
  // configure dialog (Task #2155) — the launcher depends on the analytics
  // account mapping, so its toggle lives there now. Same setting underneath.
];

// Task #1307 — global Ads Express feature gate (master-only). OFF (default) =
// the July 10 baseline output pipeline; ON = the enhanced pipeline (location
// plates, product/logo vision QA gates, template-reference leak gate, band
// text QA, inline-edit QA extras). Snapshotted per run/edit job at creation,
// so flipping it never changes in-flight work.
function AdsExpressPipelineCard() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useQuery<{ enhancedPipeline: boolean; recentGenerationsPanel?: boolean }>({
    queryKey: ["/api/admin/ads-express-settings"],
  });

  const save = useMutation({
    mutationFn: async (enhancedPipeline: boolean) => {
      const res = await apiRequest("PUT", "/api/admin/ads-express-settings", { enhancedPipeline });
      return res.json();
    },
    onSuccess: (data: { enhancedPipeline: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-settings"] });
      toast({
        title: "Saved",
        description: data.enhancedPipeline
          ? "Ads Express is now using the enhanced pipeline."
          : "Ads Express is now using the baseline (July 10) pipeline.",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  // Task #2046 follow-up — global Recent generations panel switch.
  const saveRecentPanel = useMutation({
    mutationFn: async (recentGenerationsPanel: boolean) => {
      const res = await apiRequest("PUT", "/api/admin/ads-express-settings", { recentGenerationsPanel });
      return res.json();
    },
    onSuccess: (data: { recentGenerationsPanel: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-settings"] });
      toast({
        title: "Saved",
        description: data.recentGenerationsPanel
          ? "Recent generations panel is now shown to master admins on Ads Express."
          : "Recent generations panel is off — master admins see the standard Previous Sessions panel.",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const enabled = settings?.enhancedPipeline === true;
  const recentPanelEnabled = settings?.recentGenerationsPanel === true;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Ads Express Pipeline
        </CardTitle>
        <CardDescription>
          Global switch for the Ads Express output pipeline. Off = the July 10 baseline behavior.
          On = the enhanced pipeline: location reference plates, product/logo vision QA checks with
          bounded re-rolls, template text-leak gate, 9:16 band text check, and stricter QA on inline
          image edits. Applies to new runs and edits only — in-flight work keeps the setting it
          started with.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Enhanced pipeline</div>
            <div className="text-xs text-muted-foreground">
              {enabled ? "On — new runs use the enhanced pipeline." : "Off — new runs use the July 10 baseline."}
            </div>
          </div>
          <Switch
            checked={enabled}
            disabled={isLoading || save.isPending}
            onCheckedChange={(checked) => save.mutate(checked)}
            data-testid="switch-ads-express-enhanced-pipeline"
          />
        </div>
        {/* Task #2046 follow-up — GLOBAL switch for the master-only enriched
            Recent generations table on Ads Express (not per-brand). */}
        <div className="mt-4 flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Recent generations panel</div>
            <div className="text-xs text-muted-foreground">
              {recentPanelEnabled
                ? "On — master admins see the enriched Recent generations table on Ads Express."
                : "Off — master admins see the standard Previous Sessions panel."}
            </div>
          </div>
          <Switch
            checked={recentPanelEnabled}
            disabled={isLoading || saveRecentPanel.isPending}
            onCheckedChange={(checked) => saveRecentPanel.mutate(checked)}
            data-testid="switch-ads-express-recent-generations-panel"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function BrandFeaturesTab({ allBrands }: { allBrands: Brand[] }) {
  const { toast } = useToast();

  const toggleMutation = useMutation({
    mutationFn: async ({ brandId, key, value }: { brandId: string; key: string; value: boolean }) => {
      const brand = allBrands.find(b => b.id === brandId);
      const currentSettings = (brand?.settings || {}) as Record<string, boolean>;
      await apiRequest("PATCH", `/api/brands/${brandId}`, {
        settings: { ...currentSettings, [key]: value },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brands"] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
      toast({
        title: "Feature flag updated",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update", description: e.message, variant: "destructive" });
    },
  });

  if (allBrands.length === 0) {
    return <p className="text-sm text-muted-foreground">No brands found</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium" data-testid="text-brand-features-title">Brand Feature Flags</h2>
        <p className="text-sm text-muted-foreground">Enable or disable optional features per brand. Disabled features are hidden from the sidebar.</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Brand</TableHead>
            {BRAND_FEATURE_FLAGS.map(f => (
              <TableHead key={f.key} className="text-center">{f.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {allBrands.filter(b => b.status !== "archived").map(brand => {
            const settings = (brand.settings || {}) as Record<string, boolean>;
            return (
              <TableRow key={brand.id} data-testid={`row-brand-features-${brand.id}`}>
                <TableCell className="font-medium">{brand.name}</TableCell>
                {BRAND_FEATURE_FLAGS.map(f => (
                  <TableCell key={f.key} className="text-center">
                    <Switch
                      checked={!!settings[f.key]}
                      onCheckedChange={(checked) => toggleMutation.mutate({ brandId: brand.id, key: f.key, value: checked })}
                      disabled={toggleMutation.isPending}
                      data-testid={`toggle-${f.key}-${brand.id}`}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// Mirrors server/lib/templateBrandGate.ts normalizeBrandKey so the matrix
// checks match enforcement exactly.
function normalizeLpBrandKey(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function lpBrandInList(list: string[] | null | undefined, brand: Brand): boolean {
  if (!list || list.length === 0) return true; // global
  const keys = new Set([normalizeLpBrandKey(brand.slug), normalizeLpBrandKey(brand.name)].filter(Boolean));
  return list.some((s) => keys.has(normalizeLpBrandKey(s)));
}

function LpTemplatesTab({ allBrands }: { allBrands: Brand[] }) {
  const [templateSearch, setTemplateSearch] = useState("");

  const { data: templates = [], isLoading } = useQuery<LandingPageTemplateType[]>({
    queryKey: ["/api/admin/landing-page-template-types"],
  });

  const activeBrands = allBrands.filter((b) => b.status !== "archived");

  const q = templateSearch.trim().toLowerCase();
  const visibleTemplates = q
    ? templates.filter((t) => t.label.toLowerCase().includes(q) || t.key.toLowerCase().includes(q))
    : templates;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium" data-testid="text-lp-templates-title">Landing Page Templates</h2>
        <p className="text-sm text-muted-foreground">
          Control which brands can use each landing page template. Changes take effect immediately — no restart needed. By default a template is available to every brand; turning off Active hides it from everyone.
        </p>
      </div>
      {templates.length > 5 && (
        <Input
          placeholder="Search templates..."
          value={templateSearch}
          onChange={(e) => setTemplateSearch(e.target.value)}
          className="max-w-sm"
          data-testid="input-lp-template-search"
        />
      )}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No landing page templates found</p>
      ) : visibleTemplates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No templates match "{templateSearch}"</p>
      ) : (
        <div className="space-y-3">
          {visibleTemplates.map((tmpl) => (
            <LpTemplateCard key={tmpl.id} tmpl={tmpl} activeBrands={activeBrands} />
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Use "Brands" to limit a template to specific brands. "All brands" makes it available to everyone again. A limited template needs at least one brand; use Active to hide a template from everyone.
      </p>
    </div>
  );
}

function LpTemplateCard({ tmpl, activeBrands }: { tmpl: LandingPageTemplateType; activeBrands: Brand[] }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const renameMutation = useMutation({
    mutationFn: async (label: string) => {
      await apiRequest("PATCH", `/api/admin/landing-page-template-types/${tmpl.id}`, { label });
    },
    onMutate: async (label: string) => {
      // Optimistic rename: swap the label in the admin list immediately;
      // rollback on failure.
      await queryClient.cancelQueries({ queryKey: ["/api/admin/landing-page-template-types"] });
      const previous = queryClient.getQueryData<LandingPageTemplateType[]>(["/api/admin/landing-page-template-types"]);
      queryClient.setQueryData<LandingPageTemplateType[]>(
        ["/api/admin/landing-page-template-types"],
        (old) => old?.map((t) => (t.id === tmpl.id ? { ...t, label } : t)),
      );
      return { previous };
    },
    onSuccess: () => {
      setEditingName(false);
      setNameError(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/landing-page-template-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-template-types"] });
    },
    onError: (e: Error, _label, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["/api/admin/landing-page-template-types"], context.previous);
      }
      // Keep edit mode open with the typed text so the user can fix it.
      setNameError(e.message.replace(/^\d+:\s*/, ""));
    },
  });

  const resetNameMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/admin/landing-page-template-types/${tmpl.id}`, { resetLabel: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/landing-page-template-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-template-types"] });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to reset name", description: e.message.replace(/^\d+:\s*/, ""), variant: "destructive" });
    },
  });

  const startNameEdit = () => {
    setNameDraft(tmpl.label);
    setNameError(null);
    setEditingName(true);
  };

  const cancelNameEdit = () => {
    setEditingName(false);
    setNameError(null);
  };

  const saveName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameError("Name cannot be empty");
      return;
    }
    if (trimmed.length > 80) {
      setNameError("Name must be 80 characters or fewer");
      return;
    }
    if (trimmed === tmpl.label) {
      cancelNameEdit();
      return;
    }
    setNameError(null);
    renameMutation.mutate(trimmed);
  };

  const updateMutation = useMutation({
    mutationFn: async (patch: { isActive?: boolean; allowedBrandSlugs?: string[] | null }) => {
      await apiRequest("PATCH", `/api/admin/landing-page-template-types/${tmpl.id}`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/landing-page-template-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-template-types"] });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update", description: e.message, variant: "destructive" });
    },
  });

  const list = tmpl.allowedBrandSlugs || [];
  const restricted = list.length > 0;

  const allowedBrands = restricted ? activeBrands.filter((b) => lpBrandInList(list, b)) : [];
  const matchedKeys = new Set(
    allowedBrands.flatMap((b) => [normalizeLpBrandKey(b.slug), normalizeLpBrandKey(b.name)]).filter(Boolean)
  );
  // Allow-list entries that don't match any active brand (archived brand,
  // renamed slug, etc.) — still shown so they can be removed.
  const orphanSlugs = restricted ? list.filter((s) => !matchedKeys.has(normalizeLpBrandKey(s))) : [];

  const brandKey = (brand: Brand) => normalizeLpBrandKey(brand.slug) || normalizeLpBrandKey(brand.name);

  const addBrand = (brand: Brand) => {
    const next = Array.from(new Set([...list.map(normalizeLpBrandKey), brandKey(brand)])).filter(Boolean);
    updateMutation.mutate({ allowedBrandSlugs: next });
  };

  const onlyBrand = (brand: Brand) => {
    const key = brandKey(brand);
    if (!key) return;
    updateMutation.mutate({ allowedBrandSlugs: [key] });
  };

  const excludeBrandFromGlobal = (brand: Brand) => {
    // Template is currently global; unchecking a brand limits it to every
    // OTHER active brand.
    const next = Array.from(new Set(
      activeBrands.filter((b) => b.id !== brand.id).map(brandKey).filter(Boolean)
    ));
    if (next.length === 0) {
      toast({
        title: "At least one brand required",
        description: "Removing every brand would make the template available to ALL brands. Turn off Active to hide it from everyone.",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutate({ allowedBrandSlugs: next });
  };

  const removeKeys = (keys: Set<string>) => {
    const next = list.map(normalizeLpBrandKey).filter((s) => s && !keys.has(s));
    if (next.length === 0) {
      toast({
        title: "At least one brand required",
        description: "Removing every brand would make the template available to ALL brands. Use \"All brands\" for that, or turn off Active to hide it from everyone.",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutate({ allowedBrandSlugs: next });
  };

  const removeBrand = (brand: Brand) => {
    removeKeys(new Set([normalizeLpBrandKey(brand.slug), normalizeLpBrandKey(brand.name)].filter(Boolean)));
  };

  const bq = brandSearch.trim().toLowerCase();
  const dialogBrands = bq
    ? activeBrands.filter((b) => b.name.toLowerCase().includes(bq) || (b.slug || "").toLowerCase().includes(bq))
    : activeBrands;

  return (
    <Card data-testid={`card-lp-template-${tmpl.key}`} className={!tmpl.isActive ? "opacity-60" : undefined}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {editingName ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveName();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelNameEdit();
                      }
                    }}
                    onBlur={(e) => {
                      // Blur without clicking save/cancel reverts — but not
                      // when focus moves to the save/cancel buttons.
                      const next = e.relatedTarget as HTMLElement | null;
                      if (next?.dataset?.lpNameControl === tmpl.key) return;
                      if (!renameMutation.isPending) cancelNameEdit();
                    }}
                    autoFocus
                    maxLength={80}
                    className="h-7 w-56 text-sm font-medium"
                    disabled={renameMutation.isPending}
                    data-testid={`input-lp-template-name-${tmpl.key}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-primary hover:text-primary"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={saveName}
                    disabled={renameMutation.isPending}
                    data-lp-name-control={tmpl.key}
                    data-testid={`button-save-lp-template-name-${tmpl.key}`}
                  >
                    {renameMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancelNameEdit}
                    disabled={renameMutation.isPending}
                    data-lp-name-control={tmpl.key}
                    data-testid={`button-cancel-lp-template-name-${tmpl.key}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="font-medium text-sm" data-testid={`text-lp-template-label-${tmpl.key}`}>{tmpl.label}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground"
                    onClick={startNameEdit}
                    data-testid={`button-edit-lp-template-name-${tmpl.key}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  {tmpl.labelCustomized && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground"
                      title="Reset name to default"
                      onClick={() => resetNameMutation.mutate()}
                      disabled={resetNameMutation.isPending}
                      data-testid={`button-reset-lp-template-name-${tmpl.key}`}
                    >
                      {resetNameMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                    </Button>
                  )}
                </>
              )}
              {!restricted && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" data-testid={`badge-lp-global-${tmpl.key}`}>
                  <Globe className="h-3 w-3 mr-0.5" /> All brands
                </Badge>
              )}
              {!tmpl.isActive && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`badge-lp-inactive-${tmpl.key}`}>
                  Inactive
                </Badge>
              )}
            </div>
            {editingName && nameError && (
              <p className="text-xs text-destructive mt-1" data-testid={`text-lp-template-name-error-${tmpl.key}`}>{nameError}</p>
            )}
            <p className="text-xs text-muted-foreground font-mono truncate">{tmpl.key}</p>

            {restricted && (
              <div className="mt-2 flex flex-wrap gap-1">
                {allowedBrands.map((brand) => (
                  <Badge key={brand.id} variant="secondary" className="text-[10px] gap-1 pr-1" data-testid={`badge-lp-brand-${tmpl.key}-${brand.id}`}>
                    {brand.name}
                    <button
                      className="hover:text-destructive transition-colors"
                      onClick={() => removeBrand(brand)}
                      disabled={updateMutation.isPending}
                      data-testid={`button-remove-lp-brand-${tmpl.key}-${brand.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {orphanSlugs.map((slug) => (
                  <Badge key={slug} variant="outline" className="text-[10px] gap-1 pr-1 text-muted-foreground" data-testid={`badge-lp-orphan-${tmpl.key}-${slug}`}>
                    {slug}
                    <button
                      className="hover:text-destructive transition-colors"
                      onClick={() => removeKeys(new Set([normalizeLpBrandKey(slug)]))}
                      disabled={updateMutation.isPending}
                      data-testid={`button-remove-lp-orphan-${tmpl.key}-${slug}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1.5 mr-1">
              <span className="text-xs text-muted-foreground">Active</span>
              <Switch
                checked={tmpl.isActive}
                onCheckedChange={(checked) => updateMutation.mutate({ isActive: checked })}
                disabled={updateMutation.isPending}
                data-testid={`toggle-lp-active-${tmpl.key}`}
              />
            </div>
            {restricted && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateMutation.mutate({ allowedBrandSlugs: null })}
                disabled={updateMutation.isPending}
                data-testid={`button-lp-unrestrict-${tmpl.key}`}
              >
                <Globe className="h-3.5 w-3.5 mr-1" /> All brands
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setBrandSearch(""); }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid={`button-manage-lp-brands-${tmpl.key}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Brands
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Brand access for "{tmpl.label}"</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                  {restricted
                    ? "Only the checked brands can use this template. Use \"Only\" to limit it to a single brand."
                    : "Every brand can use this template right now. Uncheck a brand to exclude it, or use \"Only\" to limit the template to a single brand."}
                </p>
                {activeBrands.length > 6 && (
                  <Input
                    placeholder="Search brands..."
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    data-testid={`input-lp-brand-search-${tmpl.key}`}
                  />
                )}
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {dialogBrands.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">No brands match</p>
                  ) : (
                    dialogBrands.map((brand) => {
                      const isAllowed = !restricted || lpBrandInList(list, brand);
                      return (
                        <label
                          key={brand.id}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                          data-testid={`label-lp-brand-toggle-${tmpl.key}-${brand.id}`}
                        >
                          <Checkbox
                            checked={isAllowed}
                            onCheckedChange={(checked) => {
                              if (!restricted) {
                                if (!checked) excludeBrandFromGlobal(brand);
                              } else if (checked) {
                                addBrand(brand);
                              } else {
                                removeBrand(brand);
                              }
                            }}
                            disabled={updateMutation.isPending}
                            data-testid={`checkbox-lp-brand-${tmpl.key}-${brand.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium">{brand.name}</span>
                            {brand.slug && (
                              <span className="text-xs text-muted-foreground ml-2 font-mono">{brand.slug}</span>
                            )}
                          </div>
                          {!(restricted && isAllowed && allowedBrands.length === 1 && orphanSlugs.length === 0) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs shrink-0"
                              onClick={(e) => { e.preventDefault(); onlyBrand(brand); }}
                              disabled={updateMutation.isPending}
                              data-testid={`button-lp-brand-only-${tmpl.key}-${brand.id}`}
                            >
                              Only
                            </Button>
                          )}
                          {isAllowed && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </label>
                      );
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type SessionRow = {
  sid: string;
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isMaster: boolean;
  isSuperuser: boolean;
  isApproved: boolean;
  groupId: string | null;
  groupName: string | null;
  lastSeenAt: string | null;
  firstSeenAt: string | null;
  ip: string | null;
  userAgent: string | null;
  expire: string;
  device: string;
  status: "active" | "idle";
  isCurrent: boolean;
};

type SessionsResponse = {
  active: SessionRow[];
  idle: SessionRow[];
  summary: {
    onlineUsers: number;
    activeSessions: number;
    idleSessions: number;
    idleAfterMs: number;
    refreshedAt: string;
  };
};

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "—";
  const diff = Date.now() - ts;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function userDisplay(s: SessionRow): string {
  const full = `${s.firstName || ""} ${s.lastName || ""}`.trim();
  return full || s.email || "Unnamed user";
}

function SessionRowItem({
  session,
  countForUser,
  onRevoke,
  isRevoking,
}: {
  session: SessionRow;
  countForUser: number;
  onRevoke: (sid: string) => void;
  isRevoking: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <TableRow data-testid={`row-session-${session.sid}`}>
      <TableCell>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium" data-testid={`text-session-user-${session.sid}`}>
              {userDisplay(session)}
            </span>
            {session.isMaster && (
              <Badge variant="default" className="text-[10px] px-1.5 py-0">
                <Shield className="h-3 w-3 mr-0.5" /> Master
              </Badge>
            )}
            {countForUser > 1 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0" data-testid={`badge-session-count-${session.userId}`}>
                {countForUser} sessions
              </Badge>
            )}
            {session.isCurrent && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary text-primary">
                You
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">{session.email || "No email"}</p>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-xs text-muted-foreground" data-testid={`text-session-group-${session.sid}`}>
          {session.groupName || (session.isMaster ? "Master" : "—")}
        </span>
      </TableCell>
      <TableCell>
        <div
          className="flex flex-col"
          title={session.lastSeenAt ? new Date(session.lastSeenAt).toLocaleString() : ""}
        >
          <span className="text-xs tabular-nums" data-testid={`text-session-last-seen-${session.sid}`}>
            {formatRelative(session.lastSeenAt)}
          </span>
          <span
            className="text-[10px] text-muted-foreground/70 tabular-nums"
            data-testid={`text-session-last-seen-absolute-${session.sid}`}
          >
            {session.lastSeenAt ? new Date(session.lastSeenAt).toLocaleString() : "—"}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-xs text-muted-foreground tabular-nums" data-testid={`text-session-started-${session.sid}`}>
          {session.firstSeenAt ? new Date(session.firstSeenAt).toLocaleString() : "—"}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Monitor className="h-3 w-3" />
          <span data-testid={`text-session-device-${session.sid}`}>{session.device}</span>
        </div>
        {session.ip && (
          <span className="text-[10px] text-muted-foreground/70 tabular-nums">{session.ip}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-destructive"
              disabled={session.isCurrent || isRevoking}
              title={session.isCurrent ? "Cannot revoke your own current session" : "Revoke session"}
              data-testid={`button-revoke-session-${session.sid}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Revoke
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke this session?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {userDisplay(session)} will be logged out on their next request. Other sessions for this user remain active.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="rounded-full"
                onClick={() => {
                  onRevoke(session.sid);
                  setConfirmOpen(false);
                }}
                data-testid={`button-confirm-revoke-${session.sid}`}
              >
                Revoke
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}

function SessionsTab() {
  const { toast } = useToast();
  const [showIdle, setShowIdle] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery<SessionsResponse>({
    queryKey: ["/api/admin/sessions"],
    refetchInterval: 15_000,
  });

  const revokeMutation = useMutation({
    mutationFn: async (sid: string) => {
      await apiRequest("DELETE", `/api/admin/sessions/${encodeURIComponent(sid)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sessions"] });
      toast({ title: "Session revoked" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to revoke session", description: e.message, variant: "destructive" });
    },
  });

  const active = data?.active || [];
  const idle = data?.idle || [];
  const counts = new Map<string, number>();
  for (const s of [...active, ...idle]) {
    counts.set(s.userId, (counts.get(s.userId) || 0) + 1);
  }

  return (
    <div className="space-y-4" data-testid="section-sessions">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Wifi className="h-5 w-5 text-emerald-500" />
            <div>
              <p className="text-2xl font-bold tabular-nums" data-testid="text-online-users">
                {data?.summary.onlineUsers ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground">Online users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-2xl font-bold tabular-nums" data-testid="text-active-sessions">
                {data?.summary.activeSessions ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground">Active sessions</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Last refreshed</p>
              <p className="text-xs font-medium tabular-nums" data-testid="text-sessions-refreshed">
                {data?.summary.refreshedAt ? formatRelative(data.summary.refreshedAt) : "—"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium" data-testid="text-active-sessions-title">Active now</h2>
          <p className="text-xs text-muted-foreground">
            Sessions seen within the last {Math.round((data?.summary.idleAfterMs ?? 15 * 60 * 1000) / 60000)} minutes.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full gap-1.5"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-refresh-sessions"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="text-no-active-sessions">
          No one is active right now.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Device</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.map((s) => (
                <SessionRowItem
                  key={s.sid}
                  session={s}
                  countForUser={counts.get(s.userId) || 1}
                  onRevoke={(sid) => revokeMutation.mutate(sid)}
                  isRevoking={revokeMutation.isPending}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setShowIdle((v) => !v)}
          data-testid="button-toggle-idle-sessions"
        >
          {showIdle ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Idle (still logged in) — {idle.length}
        </button>
        {showIdle && (
          <div className="mt-3">
            {idle.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-idle-sessions">
                No idle sessions.
              </p>
            ) : (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead>Last active</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {idle.map((s) => (
                      <SessionRowItem
                        key={s.sid}
                        session={s}
                        countForUser={counts.get(s.userId) || 1}
                        onRevoke={(sid) => revokeMutation.mutate(sid)}
                        isRevoking={revokeMutation.isPending}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type SlideshowConfigStatus = {
  simApiKey: boolean;
  kie: boolean;
  r2: boolean;
  strategistUrl: boolean;
  conceptUrl: boolean;
  copyUrl: boolean;
  storyboardUrl: boolean;
  imagePromptUrl: boolean;
  captionUrl: boolean;
  randomConceptUrl: boolean;
};

type SlideshowSettings = {
  strategistUrl: string;
  conceptUrl: string;
  copyUrl: string;
  storyboardUrl: string;
  imagePromptUrl: string;
  captionUrl: string;
  randomConceptUrl: string;
};

const SLIDESHOW_URL_FIELDS: Array<{ key: keyof SlideshowSettings; label: string; help: string }> = [
  { key: "strategistUrl", label: "Creative Strategist", help: "Runs automatically before the Concept agent (no approval gate); its creative strategy steers concept generation." },
  { key: "conceptUrl", label: "Slideshow Concept", help: "Writes each run's distinct concept (reviewed and approved before any copy is written)." },
  { key: "copyUrl", label: "Slideshow Copy", help: "Plans the angle, hook, slide count, and writes the on-slide text." },
  { key: "storyboardUrl", label: "Storyboard", help: "Writes the storyboard image prompt from the approved concept + final slideshow text (reviewed and approved before any slide images render)." },
  { key: "imagePromptUrl", label: "Image Prompt + Render Guidance", help: "Generates image prompts and text-overlay guidance." },
  { key: "captionUrl", label: "TikTok Caption + Final Package", help: "Writes the caption, hashtags, and pinned comment." },
  { key: "randomConceptUrl", label: "Random Concept", help: "Generates the one-off \"Random concept\" sentence on the create form from the brand DNA." },
];

function ConfigDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`config-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      ) : (
        <XCircle className="h-4 w-4 text-destructive" />
      )}
      <span className={ok ? "" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}

function SlideshowExpressTab() {
  const { toast } = useToast();
  const [draft, setDraft] = useState<SlideshowSettings | null>(null);

  const { data: status } = useQuery<SlideshowConfigStatus>({
    queryKey: ["/api/admin/slideshow-express-sim-config-status"],
  });
  const { data: settings } = useQuery<SlideshowSettings>({
    queryKey: ["/api/admin/slideshow-express-settings"],
  });

  const current = draft ?? settings ?? { strategistUrl: "", conceptUrl: "", copyUrl: "", storyboardUrl: "", imagePromptUrl: "", captionUrl: "", randomConceptUrl: "" };

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/admin/slideshow-express-settings", current);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/slideshow-express-sim-config-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/slideshow-express-settings"] });
      setDraft(null);
      toast({ title: "Saved", description: "Slideshow Express Sim URLs updated." });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const setField = (key: keyof SlideshowSettings, value: string) =>
    setDraft({ ...current, [key]: value });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Images className="h-5 w-5" />
            Slideshow Express Configuration
          </CardTitle>
          <CardDescription>
            Slideshow Express chains six Sim workflows whose URLs are stored in the app database (not
            environment variables). Rendering uses KIE and durable storage uses Cloudflare R2.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <ConfigDot ok={!!status?.simApiKey} label="Sim API key" />
            <ConfigDot ok={!!status?.kie} label="KIE" />
            <ConfigDot ok={!!status?.r2} label="R2 storage" />
          </div>

          <div className="space-y-4">
            {SLIDESHOW_URL_FIELDS.map((f) => {
              const configured = !!status?.[f.key];
              return (
                <div key={f.key} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`slideshow-${f.key}`} className="text-sm font-medium">{f.label}</Label>
                    {configured ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-destructive" />
                    )}
                  </div>
                  <Input
                    id={`slideshow-${f.key}`}
                    value={current[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder="https://sim.ai/api/workflows/.../execute"
                    data-testid={`input-slideshow-${f.key}`}
                  />
                  <p className="text-xs text-muted-foreground">{f.help}</p>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !draft}
              data-testid="button-save-slideshow-settings"
            >
              {save.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : (
                <><Check className="mr-2 h-4 w-4" /> Save URLs</>
              )}
            </Button>
            {draft && (
              <Button variant="ghost" onClick={() => setDraft(null)} data-testid="button-reset-slideshow-settings">
                Cancel
              </Button>
            )}
            <p className="text-xs text-muted-foreground">Leave a field empty to clear that workflow slot.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Onboarding Recipe (master-only) ─────────────────────────────────────────
// Configures exactly which ad units / meme templates / slideshow styles /
// UGC styles the machine-to-machine onboarding service runs for every
// external onboarding — as THREE per-persona sections (Persona 1/2/3,
// matching the three personas the external repl sends in intake order), each
// capped at a combined 10 selections. The run generates EXACTLY each
// section's selections for its persona. Selections are stored by STABLE key
// (slug / template id / style value) and resolved to live catalog rows at
// run time — entries that no longer resolve are surfaced as warnings here
// and skipped at orchestration time.

const RECIPE_SECTION_COUNT = 3;
const RECIPE_SECTION_CAP = 10;

type OnboardingRecipeSectionDraft = {
  adUnitSlugs: string[];
  memeTemplateIds: string[];
  slideshowStyleValues: string[];
  ugcStyleValues: string[];
};

type OnboardingRecipeDraft = {
  personaSections: OnboardingRecipeSectionDraft[];
};

const emptyRecipeSection = (): OnboardingRecipeSectionDraft => ({
  adUnitSlugs: [],
  memeTemplateIds: [],
  slideshowStyleValues: [],
  ugcStyleValues: [],
});

const countRecipeSection = (s: OnboardingRecipeSectionDraft) =>
  s.adUnitSlugs.length +
  s.memeTemplateIds.length +
  s.slideshowStyleValues.length +
  s.ugcStyleValues.length;

type OnboardingRecipeResponse = {
  settings: OnboardingRecipeSectionDraft & {
    personaSections?: OnboardingRecipeSectionDraft[];
  };
  warnings: OnboardingRecipeSectionDraft;
  sectionWarnings?: OnboardingRecipeSectionDraft[];
  serviceKeyConfigured: boolean;
  catalogs: {
    adUnits: { slug: string; name: string }[];
    memeTemplates: { templateId: string; name: string; url: string }[];
    slideshowStyles: { value: string; label: string }[];
    ugcStyles: { value: string; name: string }[];
  };
};

function OnboardingRecipePicker({
  title,
  description,
  options,
  selected,
  unresolved,
  onToggle,
  onRemoveUnresolved,
  testIdPrefix,
  disableUnselected,
}: {
  title: string;
  description: string;
  options: { key: string; label: string }[];
  selected: string[];
  unresolved: string[];
  onToggle: (key: string) => void;
  onRemoveUnresolved: (key: string) => void;
  testIdPrefix: string;
  // Section at its 10-selection cap: block ADDING (unselected boxes disable),
  // removing stays possible.
  disableUnselected?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {title}
          <Badge variant="secondary">{selected.length} selected</Badge>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {unresolved.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-2">
            <p className="text-xs font-medium text-destructive flex items-center gap-1.5">
              <XCircle className="h-3.5 w-3.5" />
              {unresolved.length} selected {unresolved.length === 1 ? "entry" : "entries"} no longer
              resolve{unresolved.length === 1 ? "s" : ""} and will be skipped:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {unresolved.map((key) => (
                <Badge key={key} variant="destructive" className="gap-1">
                  {key}
                  <button
                    onClick={() => onRemoveUnresolved(key)}
                    data-testid={`${testIdPrefix}-remove-unresolved-${key}`}
                    aria-label={`Remove ${key}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto rounded-md border divide-y">
          {options.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">No entries available.</p>
          )}
          {options.map((opt) => {
            const checked = selected.includes(opt.key);
            const blocked = !!disableUnselected && !checked;
            return (
              <label
                key={opt.key}
                className={`flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted/50 ${blocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                data-testid={`${testIdPrefix}-option-${opt.key}`}
              >
                <Checkbox
                  checked={checked}
                  disabled={blocked}
                  onCheckedChange={() => onToggle(opt.key)}
                />
                <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[180px]">{opt.key}</span>
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function OnboardingRecipeTab() {
  const { toast } = useToast();
  const [draft, setDraft] = useState<OnboardingRecipeDraft | null>(null);

  const { data, isLoading, error, refetch, isRefetching } = useQuery<OnboardingRecipeResponse>({
    queryKey: ["/api/admin/onboarding-recipe"],
  });

  // Server settings → three persona sections. Rows saved before per-persona
  // sections carry no personaSections; derive three identical sections from
  // the legacy global lists so existing selections carry over (same fallback
  // rule as the server).
  const serverSections: OnboardingRecipeSectionDraft[] = useMemo(() => {
    const s = data?.settings;
    if (!s) return Array.from({ length: RECIPE_SECTION_COUNT }, emptyRecipeSection);
    const source =
      Array.isArray(s.personaSections) && s.personaSections.length
        ? s.personaSections
        : Array.from({ length: RECIPE_SECTION_COUNT }, () => s);
    return Array.from({ length: RECIPE_SECTION_COUNT }, (_, i) => ({
      adUnitSlugs: source[i]?.adUnitSlugs ?? [],
      memeTemplateIds: source[i]?.memeTemplateIds ?? [],
      slideshowStyleValues: source[i]?.slideshowStyleValues ?? [],
      ugcStyleValues: source[i]?.ugcStyleValues ?? [],
    }));
  }, [data]);

  const current: OnboardingRecipeSectionDraft[] = draft?.personaSections ?? serverSections;
  const sectionCounts = current.map(countRecipeSection);
  // Over-cap can only come from a pre-seeded legacy row (3 × the old global
  // list); the UI blocks adding past 10 and the server rejects >10 saves.
  const overCap = sectionCounts.some((c) => c > RECIPE_SECTION_CAP);

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/admin/onboarding-recipe", {
        personaSections: current,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/onboarding-recipe"] });
      setDraft(null);
      toast({ title: "Saved", description: "Onboarding recipe updated." });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const toggle = (sectionIdx: number, field: keyof OnboardingRecipeSectionDraft, key: string) => {
    const section = current[sectionIdx];
    const selected = section[field].includes(key);
    // Client-side cap: at 10 combined selections, block ADDING (removal is
    // always allowed). Checkboxes are also disabled at the cap.
    if (!selected && countRecipeSection(section) >= RECIPE_SECTION_CAP) return;
    setDraft({
      personaSections: current.map((s, i) =>
        i === sectionIdx
          ? {
              ...s,
              [field]: selected ? s[field].filter((k) => k !== key) : [...s[field], key],
            }
          : s,
      ),
    });
  };

  // Unresolved = selected keys missing from the live catalogs, per section
  // (computed on the CURRENT draft so removals clear warnings immediately).
  const catalogs = data?.catalogs;
  const unresolved: OnboardingRecipeSectionDraft[] = useMemo(() => {
    if (!catalogs) return Array.from({ length: RECIPE_SECTION_COUNT }, emptyRecipeSection);
    const live = {
      adUnitSlugs: new Set(catalogs.adUnits.map((u) => u.slug)),
      memeTemplateIds: new Set(catalogs.memeTemplates.map((t) => t.templateId)),
      slideshowStyleValues: new Set(catalogs.slideshowStyles.map((s) => s.value)),
      ugcStyleValues: new Set(catalogs.ugcStyles.map((s) => s.value)),
    };
    const missing = (sel: string[], set: Set<string>) => sel.filter((k) => !set.has(k));
    return current.map((s) => ({
      adUnitSlugs: missing(s.adUnitSlugs, live.adUnitSlugs),
      memeTemplateIds: missing(s.memeTemplateIds, live.memeTemplateIds),
      slideshowStyleValues: missing(s.slideshowStyleValues, live.slideshowStyleValues),
      ugcStyleValues: missing(s.ugcStyleValues, live.ugcStyleValues),
    }));
  }, [catalogs, current]);

  const totalOutputs = sectionCounts.reduce((a, b) => a + b, 0);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  // Real error state: without this, a failed GET silently rendered the normal
  // editor with every catalog empty ("No entries available", 0 selected).
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Failed to load onboarding recipe
          </CardTitle>
          <CardDescription data-testid="text-recipe-load-error">
            {error instanceof Error ? error.message : String(error)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isRefetching}
            data-testid="button-retry-onboarding-recipe"
          >
            {isRefetching ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retrying…</>
            ) : (
              <><RefreshCw className="mr-2 h-4 w-4" /> Retry</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Onboarding Recipe
          </CardTitle>
          <CardDescription>
            Exactly what the external onboarding service generates for every new brand, per intake
            persona: Persona 1 gets section 1's selections, Persona 2 → section 2, Persona 3 →
            section 3 — up to {RECIPE_SECTION_CAP} outputs each. Selections reference stable keys
            and survive template-library changes — entries that stop resolving are flagged below
            and skipped at run time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <ConfigDot ok={!!data?.serviceKeyConfigured} label="Service API key" />
          </div>
          <div className="rounded-md border p-3 text-sm space-y-1">
            <p data-testid="text-recipe-persona-totals">
              {sectionCounts.map((c, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-muted-foreground"> · </span>}
                  Persona {i + 1}: <span className="font-medium">{c}</span>
                </span>
              ))}
              <span className="text-muted-foreground"> · </span>
              <span className="font-medium">{totalOutputs}</span> outputs per run
            </p>
            <p className="text-muted-foreground">
              Each persona section runs exactly its selections — no random sampling. Full sections
              ({RECIPE_SECTION_CAP} each) = {RECIPE_SECTION_CAP * RECIPE_SECTION_COUNT} outputs per
              onboarding.
            </p>
            {overCap && (
              <p className="text-destructive" data-testid="text-recipe-over-cap">
                A section exceeds the {RECIPE_SECTION_CAP}-selection cap (carried over from the old
                global recipe) — onboarding kickoffs are rejected until it's trimmed. Remove
                selections, then save.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !draft || overCap}
              data-testid="button-save-onboarding-recipe"
            >
              {save.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : (
                <><Check className="mr-2 h-4 w-4" /> Save Recipe</>
              )}
            </Button>
            {draft && (
              <Button variant="ghost" onClick={() => setDraft(null)} data-testid="button-reset-onboarding-recipe">
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {current.map((section, i) => {
        const count = sectionCounts[i];
        const atCap = count >= RECIPE_SECTION_CAP;
        const p = `recipe-p${i + 1}`;
        return (
          <div key={i} className="rounded-lg border p-4 space-y-4" data-testid={`section-recipe-persona-${i + 1}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Persona {i + 1}</h3>
                <p className="text-sm text-muted-foreground">
                  Intake persona {i + 1} (in intake order) gets exactly these selections.
                </p>
              </div>
              <Badge
                variant={count > RECIPE_SECTION_CAP ? "destructive" : atCap ? "default" : "secondary"}
                data-testid={`badge-${p}-count`}
              >
                {count} of {RECIPE_SECTION_CAP} selected
              </Badge>
            </div>
            <div className="grid gap-6 xl:grid-cols-2">
              <OnboardingRecipePicker
                title="Ads Express units"
                description="All selected units render in ONE Ads Express generation for this persona."
                options={(catalogs?.adUnits ?? []).map((u) => ({
                  // Same display name Ads Express shows: short name from the
                  // shared slug map, falling back to the DB name (shortNameOf).
                  key: u.slug,
                  label: shortNameOf(u.slug, u.name),
                }))}
                selected={section.adUnitSlugs}
                unresolved={unresolved[i].adUnitSlugs}
                onToggle={(k) => toggle(i, "adUnitSlugs", k)}
                onRemoveUnresolved={(k) => toggle(i, "adUnitSlugs", k)}
                testIdPrefix={`${p}-ad-unit`}
                disableUnselected={atCap}
              />
              <OnboardingRecipePicker
                title="Slideshow Express styles"
                description="One slideshow per selected style for this persona."
                options={(catalogs?.slideshowStyles ?? []).map((s) => ({ key: s.value, label: s.label }))}
                selected={section.slideshowStyleValues}
                unresolved={unresolved[i].slideshowStyleValues}
                onToggle={(k) => toggle(i, "slideshowStyleValues", k)}
                onRemoveUnresolved={(k) => toggle(i, "slideshowStyleValues", k)}
                testIdPrefix={`${p}-slideshow-style`}
                disableUnselected={atCap}
              />
              <OnboardingRecipePicker
                title="Meme templates"
                description="One meme per selected template (memes take a product, not an angle)."
                options={(catalogs?.memeTemplates ?? []).map((t) => ({ key: t.templateId, label: t.name }))}
                selected={section.memeTemplateIds}
                unresolved={unresolved[i].memeTemplateIds}
                onToggle={(k) => toggle(i, "memeTemplateIds", k)}
                onRemoveUnresolved={(k) => toggle(i, "memeTemplateIds", k)}
                testIdPrefix={`${p}-meme-template`}
                disableUnselected={atCap}
              />
              <OnboardingRecipePicker
                title="UGC Express styles"
                description="One UGC video per selected style (UGC takes no narrative angle)."
                options={(catalogs?.ugcStyles ?? []).map((s) => ({ key: s.value, label: s.name }))}
                selected={section.ugcStyleValues}
                unresolved={unresolved[i].ugcStyleValues}
                onToggle={(k) => toggle(i, "ugcStyleValues", k)}
                onRemoveUnresolved={(k) => toggle(i, "ugcStyleValues", k)}
                testIdPrefix={`${p}-ugc-style`}
                disableUnselected={atCap}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// The Story & Video Styles pane hosts the Story Categories and Video Styles
// libraries (moved out of Meme Settings into their own admin section).
// ?sub= preselects one — the old /story-categories and /video-express-styles
// deep links redirect with it set.
const STORY_VIDEO_SUB_TABS = new Set(["story-categories", "video-styles"]);

function StoryVideoStylesPane() {
  const [subTab, setSubTab] = useState<string>(() => {
    const s = new URLSearchParams(window.location.search).get("sub");
    return s && STORY_VIDEO_SUB_TABS.has(s) ? s : "story-categories";
  });
  return (
    <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
      <TabsList data-testid="story-video-styles-subtabs">
        <TabsTrigger value="story-categories" className="gap-1.5" data-testid="subtab-story-categories">
          <BookText className="h-4 w-4" />
          Story Categories
        </TabsTrigger>
        <TabsTrigger value="video-styles" className="gap-1.5" data-testid="subtab-video-styles">
          <Clapperboard className="h-4 w-4" />
          Video Styles
        </TabsTrigger>
      </TabsList>
      <TabsContent value="story-categories">
        <StoryCategoriesManager />
      </TabsContent>
      <TabsContent value="video-styles">
        <VideoExpressStylesManager />
      </TabsContent>
    </Tabs>
  );
}

// Admin sidebar tab values that can be preselected via ?tab= (deep links from
// the old /story-categories and /video-express-styles routes land on
// story-video-styles this way). Unknown values fall back to the default tab.
// Resolve the initial ?tab=/?sub= pair, mapping legacy Meme Settings deep
// links (?tab=meme-settings&sub=story-categories|video-styles) onto the new
// Story & Video Styles section instead of a now-dead sub-tab.
export default function AdminPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Masters get the full dashboard. Superusers retain admin-page eligibility,
  // but no longer have access to the now consistently master-only styles and
  // prompts group. Everyone else is bounced home.
  const isMasterUser = !!user?.isMaster;

  useEffect(() => {
    if (user && !user.isMaster && !user.isSuperuser) {
      setLocation("/");
    }
  }, [user, setLocation]);

  // Initial tab from the URL (?tab=...), captured once on mount. Legacy
  // meme-settings sub-tab deep links are remapped to the new section; a
  // non-master superuser receives no selectable panel.
  const [initialTab] = useState<string>(() => resolveInitialAdminTab(isMasterUser, window.location.search));

  const { data: allUsers = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
    enabled: isMasterUser,
  });

  const { data: allBrands = [], isLoading: brandsLoading } = useQuery<Brand[]>({
    queryKey: ["/api/admin/brands"],
    enabled: isMasterUser,
  });

  const { data: allAdUnits = [], isLoading: adUnitsLoading } = useQuery<AdUnitType[]>({
    queryKey: ["/api/admin/ad-units"],
    enabled: isMasterUser,
  });

  const { data: allAdUnitAssignments = [], isLoading: assignmentsLoading } = useQuery<BrandAdUnitAccess[]>({
    queryKey: ["/api/admin/ad-units/assignments"],
    enabled: isMasterUser,
  });

  const { data: allAdUnitApprovals = [], isLoading: approvalsLoading } = useQuery<BrandAdUnitApproval[]>({
    queryKey: ["/api/admin/ad-units/approvals"],
    enabled: isMasterUser,
  });

  const { data: allGroups = [] } = useQuery<Group[]>({
    queryKey: ["/api/admin/groups"],
    enabled: isMasterUser,
  });

  const { data: allBrandAssignments = [], isLoading: brandAssignmentsLoading } = useQuery<BrandUser[]>({
    queryKey: ["/api/admin/brand-assignments"],
    enabled: isMasterUser,
  });

  const brandAssignmentsByUser = useMemo(() => {
    const map = new Map<string, BrandUser[]>();
    for (const a of allBrandAssignments) {
      const list = map.get(a.userId);
      if (list) list.push(a);
      else map.set(a.userId, [a]);
    }
    return map;
  }, [allBrandAssignments]);

  const isLoading = usersLoading || brandsLoading;
  const isAdUnitLoading = adUnitsLoading || assignmentsLoading || approvalsLoading || brandsLoading;

  const globalAdUnits = allAdUnits.filter(au => !au.brandId);
  const brandOwnedAdUnits = allAdUnits.filter(au => au.brandId);

  if (user && !user.isMaster && !user.isSuperuser) {
    return null;
  }

  const adminTabTriggerClass = "w-full shrink-0 justify-start gap-2 px-3 py-1.5 text-sm font-normal rounded-md data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none";
  const adminTabGroupClass = "shrink-0 px-3 pt-4 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider first:pt-0 w-full text-left";

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Shield className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-admin-title">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Manage users, access, and credits</p>
          </div>
        </div>
        {isMasterUser && (
          <Button asChild size="sm" variant="outline" data-testid="button-admin-text-agent">
            <Link href="/admin/text-agent">Text Agent</Link>
          </Button>
        )}
      </div>

      <Tabs defaultValue={initialTab} className="flex flex-col md:flex-row gap-6 items-start">
        {/* Override the shared horizontal list's centering: overflowing vertical items must start at the scroll origin. */}
        <TabsList className="flex flex-col justify-start h-auto w-full md:w-52 shrink-0 bg-transparent p-0 gap-0.5 items-stretch md:sticky md:top-6 md:max-h-[calc(100vh-3rem)] md:overflow-y-auto md:pr-1" data-testid="admin-tabs">
          {isMasterUser && (<>
          <div className={adminTabGroupClass}>Users &amp; Access</div>
          <TabsTrigger value="users" className={adminTabTriggerClass} data-testid="tab-users">
            <Users className="h-4 w-4" />
            User Management
          </TabsTrigger>
          <TabsTrigger value="groups" className={adminTabTriggerClass} data-testid="tab-groups">
            <Building2 className="h-4 w-4" />
            Groups
          </TabsTrigger>
          <TabsTrigger value="sessions" className={adminTabTriggerClass} data-testid="tab-sessions">
            <Wifi className="h-4 w-4" />
            Active Sessions
          </TabsTrigger>

          <div className={adminTabGroupClass}>Brand Setup</div>
          <TabsTrigger value="brands" className={adminTabTriggerClass} data-testid="tab-brands">
            <Building2 className="h-4 w-4" />
            Brands
          </TabsTrigger>
          <TabsTrigger value="features" className={adminTabTriggerClass} data-testid="tab-features">
            <Settings className="h-4 w-4" />
            Brand Features
          </TabsTrigger>
          <TabsTrigger value="static-ad-units" className={adminTabTriggerClass} data-testid="tab-static-ad-units">
            <LayoutGrid className="h-4 w-4" />
            Static Ad Units
          </TabsTrigger>
          <TabsTrigger value="lp-templates" className={adminTabTriggerClass} data-testid="tab-lp-templates">
            <FileText className="h-4 w-4" />
            LP Templates
          </TabsTrigger>
          <div className={adminTabGroupClass}>Creative Direction</div>
          <TabsTrigger value="meme-settings" className={adminTabTriggerClass} data-testid="tab-meme-settings">
            <Laugh className="h-4 w-4" />
            Meme Settings
          </TabsTrigger>
          <TabsTrigger value="story-video-styles" className={adminTabTriggerClass} data-testid="tab-story-video-styles">
            <Clapperboard className="h-4 w-4" />
            Story &amp; Video Styles
          </TabsTrigger>
          <TabsTrigger value="slideshow-styles" className={adminTabTriggerClass} data-testid="tab-slideshow-styles">
            <Images className="h-4 w-4" />
            Slideshow Styles
          </TabsTrigger>
          <TabsTrigger value="ugc-express-styles" className={adminTabTriggerClass} data-testid="tab-ugc-express-styles">
            <Video className="h-4 w-4" />
            UGC Express Styles
          </TabsTrigger>
          <TabsTrigger value="persona-workflows" className={adminTabTriggerClass} data-testid="tab-persona-workflows">
            <Settings className="h-4 w-4" />
            Persona Workflows
          </TabsTrigger>
          <TabsTrigger value="email-section-devices" className={adminTabTriggerClass} data-testid="tab-email-section-devices">
            <FlaskConical className="h-4 w-4" />
            Email Section Devices
          </TabsTrigger>
          <TabsTrigger value="email-express-2-prompt" className={adminTabTriggerClass} data-testid="tab-email-express-2-prompt">
            <BookText className="h-4 w-4" />
            Email Express 2.0 Prompt
          </TabsTrigger>
          <TabsTrigger value="template-studio-rules" className={adminTabTriggerClass} data-testid="tab-template-studio-rules">
            <LayoutTemplate className="h-4 w-4" />
            Template Studio Rules
          </TabsTrigger>
          <TabsTrigger value="concept-boards" className={adminTabTriggerClass} data-testid="tab-concept-boards">
            <Images className="h-4 w-4" />
            Concept Boards
          </TabsTrigger>
          <TabsTrigger value="creative-iterations-video-prompt" className={adminTabTriggerClass} data-testid="tab-creative-iterations-video-prompt">
            <Video className="h-4 w-4" />
            Creative Iterations Prompt
          </TabsTrigger>
          <div className={adminTabGroupClass}>Creative Operations &amp; Insights</div>
          <TabsTrigger value="ads-express-config" className={adminTabTriggerClass} data-testid="tab-ads-express-config">
            <Zap className="h-4 w-4" />
            Ads Express
          </TabsTrigger>
          <TabsTrigger value="broll-express" className={adminTabTriggerClass} data-testid="tab-broll-express">
            <Video className="h-4 w-4" />
            B-roll Express
          </TabsTrigger>
          <TabsTrigger value="bulk-import" className={adminTabTriggerClass} data-testid="tab-bulk-import">
            <FileUp className="h-4 w-4" />
            Bulk Import
          </TabsTrigger>
          <TabsTrigger value="video-reference-assets" className={adminTabTriggerClass} data-testid="tab-video-reference-assets">
            <Clapperboard className="h-4 w-4" />
            Video Reference Ads
          </TabsTrigger>
          <TabsTrigger value="analytics" className={adminTabTriggerClass} data-testid="tab-analytics">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="creative-strategist" className={adminTabTriggerClass} data-testid="tab-creative-strategist">
            <Compass className="h-4 w-4" />
            Creative Strategist
          </TabsTrigger>
          <TabsTrigger value="narrative-v2" className={adminTabTriggerClass} data-testid="tab-narrative-v2">
            <BookText className="h-4 w-4" />
            Narrative V2
          </TabsTrigger>
          <TabsTrigger value="beta-features" className={adminTabTriggerClass} data-testid="tab-beta-features">
            <FlaskConical className="h-4 w-4" />
            Beta Features
          </TabsTrigger>
          <div className={adminTabGroupClass}>Billing &amp; Models</div>
          <TabsTrigger value="gpt-image-version" className={adminTabTriggerClass} data-testid="tab-gpt-image-version">
            <Images className="h-4 w-4" />
            GPT Image Version
          </TabsTrigger>
          <TabsTrigger value="models" className={adminTabTriggerClass} data-testid="tab-models">
            <Settings className="h-4 w-4" />
            Model Settings
          </TabsTrigger>
          <TabsTrigger value="credits" className={adminTabTriggerClass} data-testid="tab-credits">
            <Coins className="h-4 w-4" />
            Credits
          </TabsTrigger>
          <TabsTrigger value="cheatsheet" className={adminTabTriggerClass} data-testid="tab-cheatsheet">
            <DollarSign className="h-4 w-4" />
            Cost Cheatsheet
          </TabsTrigger>

          <div className={adminTabGroupClass}>System</div>
          <TabsTrigger value="slideshow-express" className={adminTabTriggerClass} data-testid="tab-slideshow-express">
            <Images className="h-4 w-4" />
            Slideshow Express
          </TabsTrigger>
          <TabsTrigger value="storage" className={adminTabTriggerClass} data-testid="tab-storage">
            <HardDrive className="h-4 w-4" />
            Storage
          </TabsTrigger>
          <TabsTrigger value="onboarding-recipe" className={adminTabTriggerClass} data-testid="tab-onboarding-recipe">
            <FlaskConical className="h-4 w-4" />
            Onboarding Recipe
          </TabsTrigger>
          </>)}
        </TabsList>

        <div className="flex-1 min-w-0 w-full">
        <TabsContent value="users" className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold" data-testid="text-user-count">{allUsers.length}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold" data-testid="text-brand-count">{allBrands.length}</p>
                  <p className="text-xs text-muted-foreground">Total Brands</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <LayoutGrid className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold" data-testid="text-adunit-count">{allAdUnits.length}</p>
                  <p className="text-xs text-muted-foreground">Total Ad Units</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <PendingApprovals users={allUsers} isLoading={isLoading} />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-medium">Users & Brand Assignments</h2>
              <AddUserDialog onCreated={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] })} />
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : allUsers.filter(u => u.isApproved !== false && !u.deletedAt).length === 0 ? (
              <p className="text-sm text-muted-foreground">No approved users found</p>
            ) : (
              <div className="space-y-3">
                {allUsers.filter(u => u.isApproved !== false && !u.deletedAt).sort((a, b) => (b.isMaster ? 1 : 0) - (a.isMaster ? 1 : 0)).map(u => (
                  <UserBrandManager key={u.id} targetUser={u as AdminUser} allBrands={allBrands} allGroups={allGroups} assignments={brandAssignmentsByUser.get(u.id) ?? []} assignmentsLoading={brandAssignmentsLoading} />
                ))}
              </div>
            )}
          </div>

          <DeactivatedUsers users={allUsers} />
        </TabsContent>

        <TabsContent value="groups" className="space-y-6">
          <GroupsTab />
        </TabsContent>

        <TabsContent value="brands" className="space-y-6">
          <BrandCategoriesTab />
        </TabsContent>

        <TabsContent value="static-ad-units" className="space-y-6">
          <div>
            <h2 className="text-lg font-medium mb-3" data-testid="text-adunit-access-title">Static Ad Unit Access &amp; Approvals</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Choose Access &amp; approvals on any global ad unit to curate it by brand. Access controls which brands can see it; approval independently controls the default Ads Express browse view and never grants access.
            </p>
            {isAdUnitLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : globalAdUnits.length === 0 && brandOwnedAdUnits.length === 0 ? (
              <p className="text-sm text-muted-foreground">No ad units found</p>
            ) : (
              <div className="space-y-3">
                <ApproveAccessibleAdUnitsForBrand
                  allBrands={allBrands}
                  allAdUnits={globalAdUnits}
                  allAssignments={allAdUnitAssignments}
                  allApprovals={allAdUnitApprovals}
                />
                {globalAdUnits.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide" data-testid="text-global-section">Global Ad Units ({globalAdUnits.length})</p>
                    {globalAdUnits.map(au => (
                      <AdUnitBrandManager key={au.id} adUnit={au} allBrands={allBrands} allAssignments={allAdUnitAssignments} allApprovals={allAdUnitApprovals} />
                    ))}
                  </>
                )}
                {brandOwnedAdUnits.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-4" data-testid="text-brand-owned-section">Brand-Owned Ad Units ({brandOwnedAdUnits.length})</p>
                    {brandOwnedAdUnits.map(au => (
                      <AdUnitBrandManager key={au.id} adUnit={au} allBrands={allBrands} allAssignments={allAdUnitAssignments} allApprovals={allAdUnitApprovals} />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="meme-settings">
          {/* isMaster gates the meme-template mutation controls, whose API
              routes are requireMaster server-side — superusers get the
              read-only template view (their editable surfaces live in the
              Story & Video Styles section, which self-gates via useAuth). */}
          <MemeSettingsSection isMaster={isMasterUser} />
        </TabsContent>

        <TabsContent value="story-video-styles">
          <StoryVideoStylesPane />
        </TabsContent>

        <TabsContent value="slideshow-styles">
          <SlideshowStyleManager inline />
        </TabsContent>

        <TabsContent value="ugc-express-styles">
          <UgcExpressStyleManager />
        </TabsContent>

        <TabsContent value="bulk-import">
          <BulkImportAdUnitsTab />
        </TabsContent>

        <TabsContent value="models">
          <ModelSettingsTab />
        </TabsContent>

        <TabsContent value="credits">
          <UserCreditsTab allUsers={allUsers} />
        </TabsContent>

        <TabsContent value="features" className="space-y-6">
          <AdsExpressPipelineCard />
          {isMasterUser && <AdsManagerDuplicationToggle />}
          <BrandFeaturesTab allBrands={allBrands} />
        </TabsContent>

        <TabsContent value="ads-express-config" className="space-y-6">
          {isMasterUser && <AdsExpressConfigTab allBrands={allBrands} />}
        </TabsContent>

        {isMasterUser && (
          <TabsContent value="persona-workflows" className="space-y-6">
            <PersonaWorkflowsConfigTab allBrands={allBrands} isLoading={brandsLoading} />
          </TabsContent>
        )}

        <TabsContent value="broll-express" className="space-y-6">
          <BrollExpressSettingsTab />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <CreativePerformanceConfigTab />
        </TabsContent>

        <TabsContent value="creative-strategist" className="space-y-6">
          <CreativeStrategistAdminPane />
        </TabsContent>
        <TabsContent value="narrative-v2" className="space-y-6">
          <NarrativeV2Rollout />
        </TabsContent>

        <TabsContent value="beta-features" className="space-y-6">
          <div>
            <h2 className="text-lg font-medium">Beta Features</h2>
            <p className="text-sm text-muted-foreground">
              Grant beta features to users. Access follows each user across every brand they can use.
            </p>
          </div>
          <BetaFeaturesManager />
        </TabsContent>

        <TabsContent value="email-section-devices" className="space-y-6">
          <EmailSectionDevicesTab />
        </TabsContent>

        <TabsContent value="email-express-2-prompt" className="space-y-6">
          <EmailExpress2PromptTab />
        </TabsContent>

        <TabsContent value="template-studio-rules" className="space-y-6">
          <TemplateStudioRulesTab />
        </TabsContent>
        <TabsContent value="concept-boards" className="space-y-6">
          <ConceptBoardModelTab />
        </TabsContent>
        <TabsContent value="gpt-image-version" className="space-y-6">
          {user?.isMaster && <GptImageVersionSetting />}
        </TabsContent>

        <TabsContent value="creative-iterations-video-prompt" className="space-y-6">
          <CreativeIterationsVideoPromptTab />
        </TabsContent>

        <TabsContent value="lp-templates">
          <LpTemplatesTab allBrands={allBrands} />
        </TabsContent>

        <TabsContent value="sessions">
          <SessionsTab />
        </TabsContent>

        <TabsContent value="slideshow-express" className="space-y-6">
          <SlideshowExpressTab />
        </TabsContent>

        <TabsContent value="onboarding-recipe" className="space-y-6">
          <OnboardingRecipeTab />
        </TabsContent>

        <TabsContent value="storage" className="space-y-6">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center">
              <div><p className="text-sm font-semibold">Shopify image repair</p><p className="text-xs text-muted-foreground">Preview and apply evidence-backed repairs for one product at a time.</p></div>
              <Button asChild variant="outline" className="border-amber-300 bg-background"><Link href="/admin/shopify-image-repair">Open repair console <ExternalLink className="ml-2 h-3.5 w-3.5" /></Link></Button>
            </CardContent>
          </Card>
          <StorageTab />
          <RegenerateFingerprintsCard />
          <BackfillCharacterBiosCard />
        </TabsContent>

        <TabsContent value="cheatsheet">
          <CostCheatsheetTab />
        </TabsContent>

        {isMasterUser ? (
          <TabsContent value="video-reference-assets" className="space-y-6">
            <VideoReferenceAssetsTab />
          </TabsContent>
        ) : null}
        </div>
      </Tabs>
    </div>
  );
}

type BulkImportResult = {
  index: number;
  slug?: string;
  name?: string;
  action: "created" | "updated" | "skipped" | "failed";
  id?: string;
  error?: string;
  missingImages?: string[];
  matchedImages?: { field: string; ref: string; matched: string }[];
};

type BulkImportResponse = {
  summary: {
    total: number;
    created: number;
    updated: number;
    failed: number;
    imagesSaved: number;
    dryRun: boolean;
  };
  results: BulkImportResult[];
};

function BulkImportAdUnitsTab() {
  const { toast } = useToast();
  const [metadataFile, setMetadataFile] = useState<File | null>(null);
  const [archiveFile, setArchiveFile] = useState<File | null>(null);
  const [response, setResponse] = useState<BulkImportResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<"dry" | "commit" | null>(null);

  async function submit(dryRun: boolean) {
    if (!metadataFile) {
      toast({ title: "Missing JSON", description: "Please choose a metadata JSON file.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    setMode(dryRun ? "dry" : "commit");
    setResponse(null);
    try {
      const fd = new FormData();
      fd.append("metadata", metadataFile);
      if (archiveFile) fd.append("archive", archiveFile);
      fd.append("dryRun", String(dryRun));
      const res = await fetch("/api/admin/ad-unit-types/bulk-import", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message ?? "Import failed");
      setResponse(json);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-units"] });
      toast({
        title: dryRun ? "Dry run complete" : "Import complete",
        description: `${json.summary.created} created, ${json.summary.updated} updated, ${json.summary.failed} failed`,
      });
    } catch (err: any) {
      toast({ title: "Bulk import failed", description: err?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      setMode(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            Bulk Import Ad Units
          </CardTitle>
          <CardDescription>
            Upload a master JSON file (one entry per ad unit) plus an optional ZIP of images. Image references in the JSON
            are looked up by filename inside the ZIP — matching by exact path or basename, case-insensitive. Existing rows
            are matched by <code>slug</code> and updated in place; new slugs are created.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-metadata">
                <span className="inline-flex items-center gap-1.5">
                  <FileJson className="h-4 w-4" /> Metadata JSON
                </span>
              </Label>
              <Input
                id="bulk-metadata"
                type="file"
                accept="application/json,.json"
                onChange={(e) => setMetadataFile(e.target.files?.[0] ?? null)}
                data-testid="input-bulk-metadata"
              />
              <p className="text-xs text-muted-foreground">
                Either an array <code>[...]</code> or an object <code>{`{ "adUnits": [...] }`}</code>.
              </p>
              {metadataFile && (
                <p className="text-xs" data-testid="text-bulk-metadata-name">
                  {metadataFile.name} · {(metadataFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-archive">
                <span className="inline-flex items-center gap-1.5">
                  <FileArchive className="h-4 w-4" /> Image archive (.zip, optional)
                </span>
              </Label>
              <Input
                id="bulk-archive"
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={(e) => setArchiveFile(e.target.files?.[0] ?? null)}
                data-testid="input-bulk-archive"
              />
              <p className="text-xs text-muted-foreground">
                Up to 200 MB. Filenames in the JSON (e.g. <code>"adgenExampleImageUrl": "teardown.jpg"</code>) must match a
                file in the ZIP.
              </p>
              {archiveFile && (
                <p className="text-xs" data-testid="text-bulk-archive-name">
                  {archiveFile.name} · {(archiveFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={!metadataFile || isSubmitting}
              onClick={() => submit(true)}
              data-testid="button-bulk-dry-run"
            >
              {isSubmitting && mode === "dry" ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-1.5" />
              )}
              Dry Run
            </Button>
            <Button
              disabled={!metadataFile || isSubmitting}
              onClick={() => submit(false)}
              data-testid="button-bulk-commit"
            >
              {isSubmitting && mode === "commit" ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4 mr-1.5" />
              )}
              Import
            </Button>
            {(metadataFile || archiveFile || response) && (
              <Button
                variant="ghost"
                disabled={isSubmitting}
                onClick={() => {
                  setMetadataFile(null);
                  setArchiveFile(null);
                  setResponse(null);
                }}
                data-testid="button-bulk-reset"
              >
                <X className="h-4 w-4 mr-1.5" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {response && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              {response.summary.dryRun ? "Dry-run results" : "Import results"}
            </CardTitle>
            <CardDescription>
              {response.summary.total} row{response.summary.total === 1 ? "" : "s"} processed · {response.summary.created}{" "}
              created · {response.summary.updated} updated · {response.summary.failed} failed
              {response.summary.imagesSaved > 0 && ` · ${response.summary.imagesSaved} image${response.summary.imagesSaved === 1 ? "" : "s"} saved`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {response.results.map((r) => (
                  <TableRow key={r.index} data-testid={`row-bulk-result-${r.index}`}>
                    <TableCell className="text-xs text-muted-foreground">{r.index + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{r.slug ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.name ?? "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.action === "failed"
                            ? "destructive"
                            : r.action === "created"
                            ? "default"
                            : r.action === "updated"
                            ? "secondary"
                            : "outline"
                        }
                        data-testid={`badge-bulk-action-${r.index}`}
                      >
                        {r.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.error && <div className="text-destructive" data-testid={`text-bulk-error-${r.index}`}>{r.error}</div>}
                      {r.missingImages && r.missingImages.length > 0 && (
                        <div className="text-amber-600 dark:text-amber-400" data-testid={`text-bulk-missing-${r.index}`}>
                          Missing image{r.missingImages.length === 1 ? "" : "s"}: {r.missingImages.join(", ")}
                        </div>
                      )}
                      {r.matchedImages && r.matchedImages.length > 0 && (
                        <details className="mt-1" data-testid={`details-bulk-matched-${r.index}`}>
                          <summary className="cursor-pointer text-emerald-600 dark:text-emerald-400 select-none">
                            ✓ {r.matchedImages.length} image{r.matchedImages.length === 1 ? "" : "s"} matched
                          </summary>
                          <ul className="mt-1 ml-2 space-y-0.5 font-mono text-[11px] text-muted-foreground">
                            {r.matchedImages.map((m, mi) => (
                              <li key={mi}>
                                <span className="text-foreground/80">{m.field}:</span> {m.ref}
                                {m.ref.toLowerCase() !== m.matched ? <span className="text-muted-foreground"> → {m.matched}</span> : null}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {!r.error && !r.missingImages?.length && !r.matchedImages?.length && <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
